import React, { Suspense, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { TamaguiProvider } from "tamagui";
import { tamaguiConfig } from "./tamagui.config";
import { createBrowserRouter, RouterProvider, Outlet, Navigate, useLocation } from "react-router-dom";
import { rememberRoute } from "./utils/lastRoute";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PostHogProvider, PostHogIdentifier } from "./contexts/PostHogContext";
import { trackRouteChanges } from "./analytics/routeTracking";
import { SessionTimeoutProvider } from "./contexts/SessionTimeoutContext";
import { ToastProvider } from "./contexts/ToastContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { BUSINESS_START_PATH } from "./components/auth/paywallScope";
import { WebNavigation } from "./components/common/WebNavigation";
import { FirmTimerSlot } from "./components/firm/FirmTimerSlot";
import { LoadingFallback } from "./components/common/LoadingFallback";
import { NotFoundPage } from "./components/common/NotFoundPage";
import { FranklinErrorPage } from "./components/common/FranklinErrorPage";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { SkipLink } from "./components/common/SkipLink";
import { PrintStyles } from "./styles/PrintStyles";
import { BusinessAccountingProvider } from "./contexts/BusinessAccountingContext";
import { BusinessProvider } from "./contexts/BusinessContext";
import { BusinessModeProvider, useBusinessAccountingFlag } from "./hooks/useBusinessDataMode";
import { AppPreferencesProvider } from "./contexts/AppPreferencesContext";
import { usePersonalSideHidden } from "./hooks/usePersonalSideHidden";
import { useBelongsToFirm, useIsFirmManager } from "./hooks/useFirmMembership";
import { useInPaidBusiness, useOwnsBusiness, useReadsUnpaidBusiness } from "./hooks/useInPaidBusiness";
import { BUSINESS_BILLING_PATH } from "./components/auth/paywallScope";
import { useFieldStaff } from "./hooks/useFieldStaff";
import { BusinessIndexRoute } from "./components/business/BusinessIndexRoute";
import { LiveBusinessShell } from "./components/business/LiveBusinessShell";
import { PreviewBanner } from "./components/business/common/PreviewBanner";
import { BusinessPreviewAnnouncement } from "./components/business/common/BusinessPreviewAnnouncement";
import { PaidUnnamedBusinessBanner } from "./components/business/common/PaidUnnamedBusinessBanner";
import { IS_DEV_OR_QA_BUILD } from "./config/environment";
import { SystemBanner, SYSTEM_BANNER_HEIGHT } from "./components/common/SystemBanner";
import styled from "styled-components";
import { LIST_IMPORTS_LIVE } from "./components/business/imports/list/listImportsLive";
import { COMBINED_REPORTING_LIVE } from "./components/reports/combining/combiningLive";
import { SEGMENTS_RELEASED } from "./components/business/segments/release";
import { DemoGate } from "./components/demo/DemoGate";
import { DemoReadOnlyListener } from "./components/demo/DemoReadOnlyListener";
import { DEMO_FRANKLIN_OFF_COPY } from "./config/demo";
import { FieldShell } from "./components/fieldService/offline/FieldShell";

// Lazy load all page components for code splitting

const LoginPage = React.lazy(() => import("./components/auth/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage = React.lazy(() => import("./components/auth/RegisterPage").then(m => ({ default: m.RegisterPage })));
const RegistrationSuccess = React.lazy(() => import("./components/auth/RegistrationSuccess").then(m => ({ default: m.RegistrationSuccess })));
const VerificationErrorPage = React.lazy(() => import("./components/auth/VerificationErrorPage").then(m => ({ default: m.VerificationErrorPage })));

const EnhancedDashboard = React.lazy(() => import("./components/dashboard/EnhancedDashboard").then(m => ({ default: m.EnhancedDashboard })));
const NetWorthPage = React.lazy(() => import("./components/networth/NetWorthPage").then(m => ({ default: m.NetWorthPage })));
const InvestmentsPage = React.lazy(() => import("./components/investments/InvestmentsPage").then(m => ({ default: m.InvestmentsPage })));
const AllHoldingsPage = React.lazy(() => import("./components/investments/AllHoldingsPage").then(m => ({ default: m.AllHoldingsPage })));
const PerformancePage = React.lazy(() => import("./components/investments/PerformancePage").then(m => ({ default: m.PerformancePage })));
const Transactions = React.lazy(() => import("./components/transactions/Transactions").then(m => ({ default: m.Transactions })));
const ConnectedAccounts = React.lazy(() => import("./components/accounts/ConnectedAccounts").then(m => ({ default: m.ConnectedAccounts })));
const AccountantRequestsPage = React.lazy(() => import("./components/accounts/AccountantRequestsPage").then(m => ({ default: m.AccountantRequestsPage })));
const AddInstitutionPage = React.lazy(() => import("./components/accounts/AddInstitutionPage").then(m => ({ default: m.AddInstitutionPage })));
const Settings = React.lazy(() => import("./components/settings/Settings").then(m => ({ default: m.Settings })));
const SpendingPage = React.lazy(() => import("./components/spending/SpendingPage").then(m => ({ default: m.SpendingPage })));
const PendingTransfers = React.lazy(() => import("./components/transfers/PendingTransfers").then(m => ({ default: m.PendingTransfers })));
const BudgetPage = React.lazy(() => import("./components/budget/BudgetPage").then(m => ({ default: m.BudgetPage })));
const CategoriesAndRulesPage = React.lazy(() => import("./components/settings/CategoriesAndRulesPage").then(m => ({ default: m.CategoriesAndRulesPage })));
const ManageCategories = React.lazy(() => import("./components/settings/ManageCategories").then(m => ({ default: m.ManageCategories })));
const QuickCategoriesSettings = React.lazy(() => import("./components/transactions/QuickCategoriesSettings").then(m => ({ default: m.QuickCategoriesSettings })));
const ManageRulesPage = React.lazy(() => import("./components/settings/ManageRulesPage").then(m => ({ default: m.ManageRulesPage })));
const EditRulePage = React.lazy(() => import("./components/transactions/EditRulePage").then(m => ({ default: m.EditRulePage })));

const ChatbotPage = React.lazy(() => import("./components/chatbot/ChatbotPage").then(m => ({ default: m.ChatbotPage })));
const PrivacyPage = React.lazy(() => import("./components/legal/PrivacyPage").then(m => ({ default: m.PrivacyPage })));
const TermsPage = React.lazy(() => import("./components/legal/TermsPage").then(m => ({ default: m.TermsPage })));
const SmsTermsPage = React.lazy(() => import("./components/public/smsTerms/SmsTermsPage").then(m => ({ default: m.SmsTermsPage })));
const SubscribePage = React.lazy(() => import("./pages/SubscribePage").then(m => ({ default: m.SubscribePage })));
const SubscriptionManagePage = React.lazy(() => import("./pages/SubscriptionManagePage").then(m => ({ default: m.SubscriptionManagePage })));
const BetaWaitlistPage = React.lazy(() => import("./pages/BetaWaitlistPage").then(m => ({ default: m.BetaWaitlistPage })));
const WelcomePage = React.lazy(() => import("./pages/WelcomePage").then(m => ({ default: m.WelcomePage })));
const NotificationsPage = React.lazy(() => import("./components/notifications/NotificationsPage"));
const DemoStartPage = React.lazy(() => import("./components/demo/DemoStartPage").then(m => ({ default: m.DemoStartPage })));
const DemoEndedPage = React.lazy(() => import("./components/demo/DemoEndedPage").then(m => ({ default: m.DemoEndedPage })));
const DesignSystemPage = React.lazy(() => import("./components/design-system/DesignSystemPage").then(m => ({ default: m.DesignSystemPage })));

// Business module (preview — sample data only, no backend endpoints)
const BusinessDashboard = React.lazy(() => import("./components/business/BusinessDashboard").then(m => ({ default: m.BusinessDashboard })));
const RulesPage = React.lazy(() => import("./components/business/rules/RulesPage").then(m => ({ default: m.RulesPage })));
const RuleDetailPage = React.lazy(() => import("./components/business/rules/RuleDetailPage").then(m => ({ default: m.RuleDetailPage })));
const RuleBuilderPage = React.lazy(() => import("./components/business/rules/RuleBuilderPage").then(m => ({ default: m.RuleBuilderPage })));
const BusinessSettingsPage = React.lazy(() => import("./components/business/settings/BusinessSettingsPage").then(m => ({ default: m.BusinessSettingsPage })));
// Administration hub sub-pages (PRD 29 §8). Each was a tab panel or a card
// inside one before it had a route of its own.
const InvoiceBrandingPage = React.lazy(() => import("./components/business/settings/InvoiceBrandingPage").then(m => ({ default: m.InvoiceBrandingPage })));
const DocumentNumberingPage = React.lazy(() => import("./components/business/settings/DocumentNumberingPage").then(m => ({ default: m.DocumentNumberingPage })));
// Sales tax (PRD 101) — core and ungated, off until a business turns it on.
const SalesTaxPage = React.lazy(() => import("./components/business/salesTax/SalesTaxPage").then(m => ({ default: m.SalesTaxPage })));
const NexusDashboardPage = React.lazy(() => import("./components/business/salesTax/nexus/NexusDashboardPage").then(m => ({ default: m.NexusDashboardPage })));
const ReturnsPage = React.lazy(() => import("./components/business/salesTax/returns/ReturnsPage").then(m => ({ default: m.ReturnsPage })));
const SalesTaxSettingsPage = React.lazy(() => import("./components/business/salesTax/SalesTaxSettingsPage").then(m => ({ default: m.SalesTaxSettingsPage })));
const BusinessProfilePage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.BusinessProfilePage })));
const BusinessesPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.BusinessesPage })));
const MembersPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.MembersPage })));
const YourAccountantPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.YourAccountantPage })));
const BillingPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.BillingPage })));
const BooksPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.BooksPage })));
const AgingBucketsPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.AgingBucketsPage })));
const LateFeesPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.LateFeesPage })));
const CancellationPolicyPage = React.lazy(() => import("./components/cancellationPolicies/CancellationPolicyPage").then(m => ({ default: m.CancellationPolicyPage })));
const CancellationFeesPage = React.lazy(() => import("./components/cancellationFees/CancellationFeesPage").then(m => ({ default: m.CancellationFeesPage })));
const PaymentsPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.PaymentsPage })));
const MarketplacePage = React.lazy(() => import("./components/business/settings/MarketplacePage").then(m => ({ default: m.MarketplacePage })));
const AccountingBasisPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.AccountingBasisPage })));
const AutoCategorizationPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.AutoCategorizationPage })));
const ActivityLogAdminPage = React.lazy(() => import("./components/business/settings/AdministrationPages").then(m => ({ default: m.ActivityLogAdminPage })));
const BulkOperationsPage = React.lazy(() => import("./components/business/bulk/BulkOperationsPage"));
const WebhooksPage = React.lazy(() => import("./components/business/webhooks/WebhooksPage"));
const BulkOperationDetailPage = React.lazy(() => import("./components/business/bulk/BulkOperationDetailPage"));
const InvitationAcceptPage = React.lazy(() => import("./components/business/InvitationAcceptPage").then(m => ({ default: m.InvitationAcceptPage })));
const BusinessStartPage = React.lazy(() => import("./pages/BusinessStartPage").then(m => ({ default: m.BusinessStartPage })));
const AccountantActivationPage = React.lazy(() => import("./components/firm/AccountantActivationPage").then(m => ({ default: m.AccountantActivationPage })));
const FirmInvitationAcceptPage = React.lazy(() => import("./components/firm/FirmInvitationAcceptPage").then(m => ({ default: m.FirmInvitationAcceptPage })));
const EngagementAcceptPage = React.lazy(() => import("./components/business/EngagementAcceptPage").then(m => ({ default: m.EngagementAcceptPage })));
const FirmWorklistPage = React.lazy(() => import("./components/firm/FirmWorklistPage").then(m => ({ default: m.FirmWorklistPage })));
const FirmCannabisPortfolioPage = React.lazy(() => import("./components/firm/FirmCannabisPortfolioPage").then(m => ({ default: m.FirmCannabisPortfolioPage })));
const FirmClientPage = React.lazy(() => import("./components/firm/FirmClientPage").then(m => ({ default: m.FirmClientPage })));
const FirmStaffPage = React.lazy(() => import("./components/firm/FirmStaffPage").then(m => ({ default: m.FirmStaffPage })));
const ClientRequestsPage = React.lazy(() => import("./components/requests/ClientRequestsPage").then(m => ({ default: m.ClientRequestsPage })));
const FirmRequestsPage = React.lazy(() => import("./components/firm/FirmRequestsPage").then(m => ({ default: m.FirmRequestsPage })));
const FirmProspectsPage = React.lazy(() => import("./components/firm/FirmProspectsPage").then(m => ({ default: m.FirmProspectsPage })));
const FirmProspectPage = React.lazy(() => import("./components/firm/FirmProspectPage").then(m => ({ default: m.FirmProspectPage })));
const BusinessProspectsPage = React.lazy(() => import("./components/business/crm/BusinessProspectsPage").then(m => ({ default: m.BusinessProspectsPage })));
const BusinessProspectPage = React.lazy(() => import("./components/business/crm/BusinessProspectPage").then(m => ({ default: m.BusinessProspectPage })));
const BusinessProspectImportPage = React.lazy(() => import("./components/business/crm/BusinessProspectImportPage").then(m => ({ default: m.BusinessProspectImportPage })));
const FirmProspectImportPage = React.lazy(() => import("./components/firm/FirmProspectImportPage").then(m => ({ default: m.FirmProspectImportPage })));
const FirmOutreachPage = React.lazy(() => import("./components/firm/FirmOutreachPage").then(m => ({ default: m.FirmOutreachPage })));
const FirmProspectAnalyticsPage = React.lazy(() => import("./components/firm/FirmProspectAnalyticsPage").then(m => ({ default: m.FirmProspectAnalyticsPage })));
const BusinessProspectAnalyticsPage = React.lazy(() => import("./components/business/crm/BusinessProspectAnalyticsPage").then(m => ({ default: m.BusinessProspectAnalyticsPage })));
const BusinessOutreachSettingsPage = React.lazy(() => import("./components/business/crm/BusinessOutreachSettingsPage").then(m => ({ default: m.BusinessOutreachSettingsPage })));
// Field service (PRD 88 onwards) — the `field_service` module. The sidebar
// draws every row for a holder and the Overview alone for a business that
// never held it (fieldServiceNavAccess).
const FieldServiceOverviewPage = React.lazy(() => import("./components/fieldService/overview/FieldServiceOverviewPage").then(m => ({ default: m.FieldServiceOverviewPage })));
const FieldSwitchGuidePage = React.lazy(() => import("./components/fieldService/imports/FieldSwitchGuidePage").then(m => ({ default: m.FieldSwitchGuidePage })));
const FieldImportPage = React.lazy(() => import("./components/fieldService/imports/FieldImportPage").then(m => ({ default: m.FieldImportPage })));
const RecurringWorklistPage = React.lazy(() => import("./components/fieldService/imports/SwitchWorklistPage").then(m => ({ default: m.RecurringWorklistPage })));
const DepositsWorklistPage = React.lazy(() => import("./components/fieldService/imports/SwitchWorklistPage").then(m => ({ default: m.DepositsWorklistPage })));
const FieldServiceSettingsPage = React.lazy(() => import("./components/fieldService/settings/FieldServiceSettingsPage").then(m => ({ default: m.FieldServiceSettingsPage })));
// PRD 89 Wave 2 — the field roster and its settings, under the same group.
const TechniciansPage = React.lazy(() => import("./components/fieldService/roster/TechniciansPage").then(m => ({ default: m.TechniciansPage })));
const TechnicianPage = React.lazy(() => import("./components/fieldService/technician/TechnicianPage").then(m => ({ default: m.TechnicianPage })));
const TimeOffPage = React.lazy(() => import("./components/fieldService/timeOff/TimeOffPage").then(m => ({ default: m.TimeOffPage })));
const VehiclesPage = React.lazy(() => import("./components/fieldService/vehicles/VehiclesPage").then(m => ({ default: m.VehiclesPage })));
const MyLocationPage = React.lazy(() => import("./components/fieldService/location/MyLocationPage").then(m => ({ default: m.MyLocationPage })));
const LocationSettingsPage = React.lazy(() => import("./components/fieldService/location/LocationSettingsPage").then(m => ({ default: m.LocationSettingsPage })));
const TechnicianLocationHistoryPage = React.lazy(() => import("./components/fieldService/location/TechnicianLocationHistoryPage").then(m => ({ default: m.TechnicianLocationHistoryPage })));
const MileagePage = React.lazy(() => import("./components/fieldService/mileage/MileagePage").then(m => ({ default: m.MileagePage })));
// PRD 103: truck stock — the grid, a location, transfers, My van, settings.
const StockPage = React.lazy(() => import("./components/fieldService/stock/StockPage").then(m => ({ default: m.StockPage })));
const StockLocationPage = React.lazy(() => import("./components/fieldService/stock/StockLocationPage").then(m => ({ default: m.StockLocationPage })));
const StockTransfersPage = React.lazy(() => import("./components/fieldService/stock/StockTransfersPage").then(m => ({ default: m.StockTransfersPage })));
const MyVanPage = React.lazy(() => import("./components/fieldService/stock/MyVanPage").then(m => ({ default: m.MyVanPage })));
const StockCountPage = React.lazy(() => import("./components/fieldService/stock/StockCountPage").then(m => ({ default: m.StockCountPage })));
const ReplenishmentPage = React.lazy(() => import("./components/fieldService/stock/ReplenishmentPage").then(m => ({ default: m.ReplenishmentPage })));
const PurchasingPage = React.lazy(() => import("./components/fieldService/purchasing/PurchasingPage").then(m => ({ default: m.PurchasingPage })));
const PurchaseOrderPage = React.lazy(() => import("./components/fieldService/purchasing/PurchaseOrderPage").then(m => ({ default: m.PurchaseOrderPage })));
const StockSettingsPage = React.lazy(() => import("./components/fieldService/stock/StockSettingsPage").then(m => ({ default: m.StockSettingsPage })));
// PRD 106: equipment at the house, its page, permits and the settings page.
const EquipmentListPage = React.lazy(() => import("./components/fieldService/equipment/EquipmentListPage").then(m => ({ default: m.EquipmentListPage })));
const EquipmentPage = React.lazy(() => import("./components/fieldService/equipment/EquipmentPage").then(m => ({ default: m.EquipmentPage })));
const EquipmentSettingsPage = React.lazy(() => import("./components/fieldService/equipment/EquipmentSettingsPage").then(m => ({ default: m.EquipmentSettingsPage })));
const PermitsPage = React.lazy(() => import("./components/fieldService/permits/PermitsPage").then(m => ({ default: m.PermitsPage })));
const MileageLogPage = React.lazy(() => import("./components/fieldService/mileage/MileageLogPage").then(m => ({ default: m.MileageLogPage })));
const MileageSettingsPage = React.lazy(() => import("./components/fieldService/mileage/MileageSettingsPage").then(m => ({ default: m.MileageSettingsPage })));
const SkillsSettingsPage = React.lazy(() => import("./components/fieldService/settings/SkillsSettingsPage").then(m => ({ default: m.SkillsSettingsPage })));
// PRD 89 Wave 3 — My profile: a technician's own row (core-safe: it answers
// "You're not on the field roster here" to anyone without one).
const MyProfilePage = React.lazy(() => import("./components/fieldService/me/MyProfilePage").then(m => ({ default: m.MyProfilePage })));
const ZonesSettingsPage = React.lazy(() => import("./components/fieldService/settings/ZonesSettingsPage").then(m => ({ default: m.ZonesSettingsPage })));
// PRD 88 Wave 2 — service locations: the list, and a location's page.
const ServiceLocationsList = React.lazy(() => import("./components/fieldService/locations/ServiceLocationsList").then(m => ({ default: m.ServiceLocationsList })));
const LocationPage = React.lazy(() => import("./components/fieldService/locations/LocationPage").then(m => ({ default: m.LocationPage })));
// PRD 90 Wave 1 — jobs, visits and job types, under the same group.
const JobsList = React.lazy(() => import("./components/fieldService/jobs/JobsList").then(m => ({ default: m.JobsList })));
const JobPage = React.lazy(() => import("./components/fieldService/jobs/JobPage").then(m => ({ default: m.JobPage })));
// PRD 98 §5 — estimates: the unsold list and one estimate (builder, issued, approved).
const RecurringList = React.lazy(() => import("./components/fieldService/recurring/RecurringList").then(m => ({ default: m.RecurringList })));
const RecurrencePage = React.lazy(() => import("./components/fieldService/recurring/RecurrencePage").then(m => ({ default: m.RecurrencePage })));
const EstimatesList = React.lazy(() => import("./components/fieldService/estimates/EstimatesList").then(m => ({ default: m.EstimatesList })));
const FieldEstimatePage = React.lazy(() => import("./components/fieldService/estimates/EstimatePage").then(m => ({ default: m.EstimatePage })));
const FieldEstimatePresentPage = React.lazy(() => import("./components/fieldService/estimates/EstimatePresentPage").then(m => ({ default: m.EstimatePresentPage })));
const FieldEstimateSettingsPage = React.lazy(() => import("./components/fieldService/estimates/EstimateSettingsPage").then(m => ({ default: m.EstimateSettingsPage })));
// PRD 102 §5 — memberships: the list, one membership, present mode, plan settings, and the roll-forward.
const MembershipsList = React.lazy(() => import("./components/fieldService/memberships/MembershipsList").then(m => ({ default: m.MembershipsList })));
const MembershipPage = React.lazy(() => import("./components/fieldService/memberships/MembershipPage").then(m => ({ default: m.MembershipPage })));
const PresentMembershipPage = React.lazy(() => import("./components/fieldService/memberships/PresentMembershipPage").then(m => ({ default: m.PresentMembershipPage })));
// PRD 110 §5 — install projects: the list, one project, and the settings.
const ProjectsList = React.lazy(() => import("./components/fieldService/projects/ProjectsList").then(m => ({ default: m.ProjectsList })));
const ProjectPage = React.lazy(() => import("./components/fieldService/projects/ProjectPage").then(m => ({ default: m.ProjectPage })));
const ProjectSettingsPage = React.lazy(() => import("./components/fieldService/projects/ProjectSettingsPage").then(m => ({ default: m.ProjectSettingsPage })));
const MembershipPlansPage = React.lazy(() => import("./components/fieldService/memberships/MembershipPlansPage").then(m => ({ default: m.MembershipPlansPage })));
const DeferredMembershipRevenuePage = React.lazy(() => import("./components/fieldService/memberships/DeferredMembershipRevenuePage").then(m => ({ default: m.DeferredMembershipRevenuePage })));
const DispatchBoardPage = React.lazy(() => import("./components/fieldService/dispatch/DispatchBoardPage").then(m => ({ default: m.DispatchBoardPage })));
const FieldRouteSheetPage = React.lazy(() => import("./components/fieldService/dispatch/RouteSheetPage").then(m => ({ default: m.RouteSheetPage })));
const FieldCalendarPage = React.lazy(() => import("./components/fieldService/dispatch/CalendarPage").then(m => ({ default: m.CalendarPage })));
const InvoicingPage = React.lazy(() => import("./components/fieldService/jobs/InvoicingPage").then(m => ({ default: m.InvoicingPage })));
const JobProfitabilityPage = React.lazy(() => import("./components/fieldService/costing/JobProfitabilityPage").then(m => ({ default: m.JobProfitabilityPage })));
const PriceBookPage = React.lazy(() => import("./components/fieldService/priceBook/PriceBookPage").then(m => ({ default: m.PriceBookPage })));
const PriceBookImportPage = React.lazy(() => import("./components/fieldService/priceBook/PriceBookImportPage").then(m => ({ default: m.PriceBookImportPage })));
const MyDayPage = React.lazy(() => import("./components/fieldService/day/MyDayPage").then(m => ({ default: m.MyDayPage })));
const MyTimePage = React.lazy(() => import("./components/fieldService/day/MyTimePage").then(m => ({ default: m.MyTimePage })));
const FieldTimePage = React.lazy(() => import("./components/fieldService/day/TimePage").then(m => ({ default: m.TimePage })));
const FieldPaymentsPage = React.lazy(() => import("./components/fieldService/payments/FieldPaymentsPage").then(m => ({ default: m.FieldPaymentsPage })));
const TimesheetsPage = React.lazy(() => import("./components/fieldService/pay/TimesheetsPage").then(m => ({ default: m.TimesheetsPage })));
const PayrollExportsPage = React.lazy(() => import("./components/fieldService/pay/PayrollExportsPage").then(m => ({ default: m.PayrollExportsPage })));
const CommissionsPage = React.lazy(() => import("./components/fieldService/pay/CommissionsPage").then(m => ({ default: m.CommissionsPage })));
const CommissionPlansPage = React.lazy(() => import("./components/fieldService/pay/CommissionPlansPage").then(m => ({ default: m.CommissionPlansPage })));
const CommissionPlanPage = React.lazy(() => import("./components/fieldService/pay/CommissionPlanPage").then(m => ({ default: m.CommissionPlanPage })));
const FieldPerformancePage = React.lazy(() => import("./components/fieldService/performance/PerformancePage").then(m => ({ default: m.PerformancePage })));
const ScorecardPage = React.lazy(() => import("./components/fieldService/performance/ScorecardPage").then(m => ({ default: m.ScorecardPage })));
const MyNumbersPage = React.lazy(() => import("./components/fieldService/performance/ScorecardPage").then(m => ({ default: m.MyNumbersPage })));
const PaySettingsPage = React.lazy(() => import("./components/fieldService/pay/PaySettingsPage").then(m => ({ default: m.PaySettingsPage })));
const FeedbackScreen = React.lazy(() => import("./components/fieldService/feedback/FeedbackScreen").then(m => ({ default: m.FeedbackScreen })));
const RecommendationsScreen = React.lazy(() => import("./components/fieldService/feedback/RecommendationsScreen").then(m => ({ default: m.RecommendationsScreen })));
const FeedbackSettingsPage = React.lazy(() => import("./components/fieldService/feedback/FeedbackSettingsPage").then(m => ({ default: m.FeedbackSettingsPage })));
const CustomerResponsesPage = React.lazy(() => import("./components/fieldService/notices/CustomerResponsesPage").then(m => ({ default: m.CustomerResponsesPage })));
const NotificationRulesPage = React.lazy(() => import("./components/fieldService/notices/NotificationRulesPage").then(m => ({ default: m.NotificationRulesPage })));
const ChecklistSettingsPage = React.lazy(() => import("./components/fieldService/checklists/ChecklistSettingsPage").then(m => ({ default: m.ChecklistSettingsPage })));
const SignVisitPage = React.lazy(() => import("./components/fieldService/signatures/SignVisitPage").then(m => ({ default: m.SignVisitPage })));
const SignatureDisclosuresPage = React.lazy(() => import("./components/fieldService/signatures/SignatureDisclosuresPage").then(m => ({ default: m.SignatureDisclosuresPage })));
const ChecklistTemplatePage = React.lazy(() => import("./components/fieldService/checklists/ChecklistTemplatePage").then(m => ({ default: m.ChecklistTemplatePage })));
const ChecklistResultsPage = React.lazy(() => import("./components/fieldService/checklists/ChecklistResultsPage").then(m => ({ default: m.ChecklistResultsPage })));
const ChecklistRunnerPage = React.lazy(() => import("./components/fieldService/checklists/ChecklistRunnerPage").then(m => ({ default: m.ChecklistRunnerPage })));
const FieldVisitPage = React.lazy(() => import("./components/fieldService/day/FieldVisitPage").then(m => ({ default: m.FieldVisitPage })));
const JobTypesSettingsPage = React.lazy(() => import("./components/fieldService/jobs/JobTypesSettingsPage").then(m => ({ default: m.JobTypesSettingsPage })));
// PRD 92 §5 — Book a job (the call-booking screen) and the Requests inbox.
const CallBookingPage = React.lazy(() => import("./components/fieldService/booking/CallBookingPage").then(m => ({ default: m.CallBookingPage })));
const BookingRequestsPage = React.lazy(() => import("./components/fieldService/booking/BookingRequestsPage").then(m => ({ default: m.BookingRequestsPage })));
const HubSettingsPage = React.lazy(() => import("./components/fieldService/hub/HubSettingsPage").then(m => ({ default: m.HubSettingsPage })));
const CardReadersSettingsPage = React.lazy(() => import("./components/fieldService/payments/CardReadersSettingsPage").then(m => ({ default: m.CardReadersSettingsPage })));
const FinancingSettingsPage = React.lazy(() => import("./components/fieldService/payments/financing/FinancingSettingsPage").then(m => ({ default: m.FinancingSettingsPage })));
const BookingPagesSettingsPage = React.lazy(() => import("./components/fieldService/booking/BookingPagesSettingsPage").then(m => ({ default: m.BookingPagesSettingsPage })));
const BookingPageEditor = React.lazy(() => import("./components/fieldService/booking/BookingPageEditor").then(m => ({ default: m.BookingPageEditor })));
const OutreachUnsubscribePage = React.lazy(() => import("./components/public/UnsubscribePage").then(m => ({ default: m.UnsubscribePage })));
const LeadFormPage = React.lazy(() => import("./components/public/LeadFormPage").then(m => ({ default: m.LeadFormPage })));
const BookingPage = React.lazy(() => import("./components/public/BookingPage").then(m => ({ default: m.BookingPage })));
const ProposalPage = React.lazy(() => import("./components/public/ProposalPage").then(m => ({ default: m.ProposalPage })));
const VisitPreviewPage = React.lazy(() => import("./components/public/VisitPreviewPage").then(m => ({ default: m.VisitPreviewPage })));
// PRD 98 §4.7 — an estimate, read, chosen from and signed by the business's customer.
const PublicEstimatePage = React.lazy(() => import("./components/public/EstimatePage").then(m => ({ default: m.EstimatePage })));
const BookingCancelPage = React.lazy(() => import("./components/public/BookingCancelPage").then(m => ({ default: m.BookingCancelPage })));
// PRD 92 §4.5 — a business's online booking page and a request's status link.
const BookOnlinePage = React.lazy(() => import("./components/public/BookOnlinePage").then(m => ({ default: m.BookOnlinePage })));
const FeedbackPage = React.lazy(() => import("./components/public/FeedbackPage").then(m => ({ default: m.FeedbackPage })));
const CardOnFilePage = React.lazy(() => import("./components/public/CardOnFilePage").then(m => ({ default: m.CardOnFilePage })));
const CustomerHubPage = React.lazy(() => import("./components/public/CustomerHubPage").then(m => ({ default: m.CustomerHubPage })));
const BookingStatusPage = React.lazy(() => import("./components/public/BookingStatusPage").then(m => ({ default: m.BookingStatusPage })));
const FirmTimePage = React.lazy(() => import("./components/firm/FirmTimePage").then(m => ({ default: m.FirmTimePage })));
const FirmCapacityPage = React.lazy(() => import("./components/firm/FirmCapacityPage").then(m => ({ default: m.FirmCapacityPage })));
const FirmDashboardPage = React.lazy(() => import("./components/firm/FirmDashboardPage").then(m => ({ default: m.FirmDashboardPage })));
const FirmTeamsPage = React.lazy(() => import("./components/firm/FirmTeamsPage").then(m => ({ default: m.FirmTeamsPage })));
const FirmWorkInsightsPage = React.lazy(() => import("./components/firm/FirmWorkInsightsPage").then(m => ({ default: m.FirmWorkInsightsPage })));
const FirmUtilisationPage = React.lazy(() => import("./components/firm/FirmUtilisationPage").then(m => ({ default: m.FirmUtilisationPage })));
const ClientMessagesPage = React.lazy(() => import("./components/business/messages/ClientMessagesPage").then(m => ({ default: m.ClientMessagesPage })));
const ClientMessageThreadPage = React.lazy(() => import("./components/business/messages/ClientMessageThreadPage").then(m => ({ default: m.ClientMessageThreadPage })));
const FirmMessagesPage = React.lazy(() => import("./components/firm/FirmMessagesPage").then(m => ({ default: m.FirmMessagesPage })));
const FirmMessageThreadPage = React.lazy(() => import("./components/firm/FirmMessageThreadPage").then(m => ({ default: m.FirmMessageThreadPage })));
const FirmBookHealthPage = React.lazy(() => import("./components/firm/FirmBookHealthPage").then(m => ({ default: m.FirmBookHealthPage })));
const FirmRatesPage = React.lazy(() => import("./components/firm/FirmRatesPage").then(m => ({ default: m.FirmRatesPage })));
const FirmClientBillingPage = React.lazy(() => import("./components/firm/FirmClientBillingPage").then(m => ({ default: m.FirmClientBillingPage })));
const FirmArrangementsPage = React.lazy(() => import("./components/firm/FirmArrangementsPage").then(m => ({ default: m.FirmArrangementsPage })));
const FirmWipPage = React.lazy(() => import("./components/firm/FirmWipPage").then(m => ({ default: m.FirmWipPage })));
const FirmRealizationPage = React.lazy(() => import("./components/firm/FirmRealizationPage").then(m => ({ default: m.FirmRealizationPage })));
const FirmLettersPage = React.lazy(() => import("./components/firm/FirmLettersPage").then(m => ({ default: m.FirmLettersPage })));
const FirmRequestComposerPage = React.lazy(() => import("./components/firm/FirmRequestComposerPage").then(m => ({ default: m.FirmRequestComposerPage })));
const FirmRequestDetailPage = React.lazy(() => import("./components/firm/FirmRequestDetailPage").then(m => ({ default: m.FirmRequestDetailPage })));
const FirmAdminPage = React.lazy(() => import("./components/firm/FirmAdminPage").then(m => ({ default: m.FirmAdminPage })));
const FirmInfoPage = React.lazy(() => import("./components/firm/FirmInfoPage").then(m => ({ default: m.FirmInfoPage })));
const FirmBillingPage = React.lazy(() => import("./components/firm/FirmBillingPage").then(m => ({ default: m.FirmBillingPage })));
const FirmMigrationBoardPage = React.lazy(() => import("./components/firm/FirmMigrationBoardPage").then(m => ({ default: m.FirmMigrationBoardPage })));
const FirmCoveragePage = React.lazy(() => import("./components/firm/FirmCoveragePage").then(m => ({ default: m.FirmCoveragePage })));
const FirmWorkBoardPage = React.lazy(() => import("./components/firm/FirmWorkBoardPage").then(m => ({ default: m.FirmWorkBoardPage })));
const FirmWorkItemPage = React.lazy(() => import("./components/firm/FirmWorkItemPage").then(m => ({ default: m.FirmWorkItemPage })));
const FirmWorkTemplatesPage = React.lazy(() => import("./components/firm/FirmWorkTemplatesPage").then(m => ({ default: m.FirmWorkTemplatesPage })));
const FirmWorkTypesPage = React.lazy(() => import("./components/firm/FirmWorkTypesPage").then(m => ({ default: m.FirmWorkTypesPage })));
const FirmWorkTemplateEditorPage = React.lazy(() => import("./components/firm/FirmWorkTemplateEditorPage").then(m => ({ default: m.FirmWorkTemplateEditorPage })));
const FirmWorkSchedulesPage = React.lazy(() => import("./components/firm/FirmWorkSchedulesPage").then(m => ({ default: m.FirmWorkSchedulesPage })));
const FirmMyWeekPage = React.lazy(() => import("./components/firm/FirmMyWeekPage").then(m => ({ default: m.FirmMyWeekPage })));
const FirmCloseBoardPage = React.lazy(() => import("./components/firm/FirmCloseBoardPage").then(m => ({ default: m.FirmCloseBoardPage })));
const W9FormPage = React.lazy(() => import("./components/business/w9/W9FormPage").then(m => ({ default: m.W9FormPage })));
const PayInvoicePage = React.lazy(() => import("./components/business/pay/PayInvoicePage").then(m => ({ default: m.PayInvoicePage })));
const OAuthConsentPage = React.lazy(() => import("./components/oauth/OAuthConsentPage").then(m => ({ default: m.OAuthConsentPage })));
const UnsubscribePage = React.lazy(() => import("./components/notifications/UnsubscribePage").then(m => ({ default: m.UnsubscribePage })));
const NotificationPreferencesPage = React.lazy(() => import("./components/notifications/NotificationPreferences").then(m => ({ default: m.NotificationPreferences })));
const ReportARepairPage = React.lazy(() => import("./components/maintenance/report/ReportARepairPage").then(m => ({ default: m.ReportARepairPage })));
const ClientRequestPage = React.lazy(() => import("./components/public/ClientRequestPage").then(m => ({ default: m.ClientRequestPage })));
const LeaseSigningPage = React.lazy(() => import("./components/leases/signing/LeaseSigningPage").then(m => ({ default: m.LeaseSigningPage })));
const MyTenancyPage = React.lazy(() => import("./components/tenant/MyTenancyPage").then(m => ({ default: m.MyTenancyPage })));
const BankingAccountsList = React.lazy(() => import("./components/banking/BankingAccountsList").then(m => ({ default: m.BankingAccountsList })));
const BankRegister = React.lazy(() => import("./components/banking/BankRegister").then(m => ({ default: m.BankRegister })));
const ReconcilePage = React.lazy(() => import("./components/banking/ReconcilePage").then(m => ({ default: m.ReconcilePage })));
const ReconciliationOverview = React.lazy(() => import("./components/banking/ReconciliationOverview").then(m => ({ default: m.ReconciliationOverview })));
const ReconciliationHistoryPage = React.lazy(() => import("./components/banking/history/ReconciliationHistoryPage").then(m => ({ default: m.ReconciliationHistoryPage })));
const ReconciliationHistoryDetailPage = React.lazy(() => import("./components/banking/history/ReconciliationHistoryDetailPage").then(m => ({ default: m.ReconciliationHistoryDetailPage })));
const ChartOfAccounts = React.lazy(() => import("./components/accounting/ChartOfAccounts").then(m => ({ default: m.ChartOfAccounts })));
const GLAccountLedger = React.lazy(() => import("./components/accounting/GLAccountLedger").then(m => ({ default: m.GLAccountLedger })));
const JournalEntriesList = React.lazy(() => import("./components/accounting/JournalEntriesList").then(m => ({ default: m.JournalEntriesList })));
const ExpenseReportsPage = React.lazy(() => import("./components/business/expenseReports/ExpenseReportsPage").then(m => ({ default: m.ExpenseReportsPage })));
const StoreSalesPage = React.lazy(() => import("./components/business/storeSales/StoreSalesPage").then(m => ({ default: m.StoreSalesPage })));
const JournalEntryDetail = React.lazy(() => import("./components/accounting/JournalEntryDetail").then(m => ({ default: m.JournalEntryDetail })));
const JournalEntryForm = React.lazy(() => import("./components/accounting/JournalEntryForm").then(m => ({ default: m.JournalEntryForm })));
const TrialBalance = React.lazy(() => import("./components/accounting/TrialBalance").then(m => ({ default: m.TrialBalance })));
const PeriodClosePage = React.lazy(() => import("./components/accounting/PeriodClosePage").then(m => ({ default: m.PeriodClosePage })));
const MonthEndClosePage = React.lazy(() => import("./components/accounting/MonthEndClosePage").then(m => ({ default: m.MonthEndClosePage })));
const RecurringJournalEntriesPage = React.lazy(() => import("./components/accounting/RecurringJournalEntriesPage").then(m => ({ default: m.RecurringJournalEntriesPage })));
const FixedAssetsList = React.lazy(() => import("./components/fixedAssets/FixedAssetsList").then(m => ({ default: m.FixedAssetsList })));
const FixedAssetDetail = React.lazy(() => import("./components/fixedAssets/FixedAssetDetail").then(m => ({ default: m.FixedAssetDetail })));
const TaxDepreciationReportPage = React.lazy(() => import("./components/fixedAssets/TaxDepreciationReportPage").then(m => ({ default: m.TaxDepreciationReportPage })));
const CustomersList = React.lazy(() => import("./components/customers/CustomersList").then(m => ({ default: m.CustomersList })));
const CustomerDetailPage = React.lazy(() => import("./components/customers/CustomerDetailPage").then(m => ({ default: m.CustomerDetailPage })));
const CustomerDepositRedirect = React.lazy(() => import("./components/customers/deposits/CustomerDepositRedirect").then(m => ({ default: m.CustomerDepositRedirect })));
const InvoicesList = React.lazy(() => import("./components/customers/InvoicesList").then(m => ({ default: m.InvoicesList })));
// Live mode only — these components redirect in preview mode
const NewInvoicePage = React.lazy(() => import("./components/customers/NewInvoicePage").then(m => ({ default: m.NewInvoicePage })));
const InvoiceDetail = React.lazy(() => import("./components/customers/InvoiceDetail").then(m => ({ default: m.InvoiceDetail })));
const UndepositedFundsPage = React.lazy(() => import("./components/customers/UndepositedFundsPage").then(m => ({ default: m.UndepositedFundsPage })));
const VendorsList = React.lazy(() => import("./components/vendors/VendorsList").then(m => ({ default: m.VendorsList })));
const VendorDetailPage = React.lazy(() => import("./components/vendors/VendorDetailPage").then(m => ({ default: m.VendorDetailPage })));
const BillsList = React.lazy(() => import("./components/vendors/BillsList").then(m => ({ default: m.BillsList })));
// Live mode only — these components redirect in preview mode
const NewBillPage = React.lazy(() => import("./components/vendors/NewBillPage").then(m => ({ default: m.NewBillPage })));
const BillDetail = React.lazy(() => import("./components/vendors/BillDetail").then(m => ({ default: m.BillDetail })));
const InventoryItemsList = React.lazy(() => import("./components/inventory/InventoryItemsList").then(m => ({ default: m.InventoryItemsList })));
const ServiceItemsList = React.lazy(() => import("./components/business/serviceItems/ServiceItemsList").then(m => ({ default: m.ServiceItemsList })));
const InventoryItemDetail = React.lazy(() => import("./components/inventory/InventoryItemDetail").then(m => ({ default: m.InventoryItemDetail })));
// The cannabis module (PRD 111 §5). Reached from its one nav row, which is
// drawn only for a business that holds the module or set it up.
const CannabisOverviewPage = React.lazy(() => import("./components/business/cannabis/CannabisOverviewPage").then(m => ({ default: m.CannabisOverviewPage })));
// Point of sale (PRD 118): the set-up page, dark behind POS_RELEASED like its nav row.
const PosSettingsPage = React.lazy(() => import("./components/pos/settings/PosSettingsPage").then(m => ({ default: m.PosSettingsPage })));
const PosRegisterPage = React.lazy(() => import("./components/pos/register/PosRegisterPage").then(m => ({ default: m.PosRegisterPage })));
const PosSalesPage = React.lazy(() => import("./components/pos/sales/PosSalesPage").then(m => ({ default: m.PosSalesPage })));
// The printable scanner setup sheet (PLAN-W3), linked from the printers card; dark with the rest of point of sale.
const ScannerSetupSheet = React.lazy(() => import("./components/pos/settings/ScannerSetupSheet").then(m => ({ default: m.ScannerSetupSheet })));
// Personal services (PRD 121 §5.2): the Overview, Set-up and Settings, dark
// behind PERSONAL_SERVICES_RELEASED like the Appointments nav group.
const PersonalServicesOverviewPage = React.lazy(() => import("./components/personalServices/overview/PersonalServicesOverviewPage").then(m => ({ default: m.PersonalServicesOverviewPage })));
const PersonalServicesSetUpPage = React.lazy(() => import("./components/personalServices/setup/PersonalServicesSetUpPage").then(m => ({ default: m.PersonalServicesSetUpPage })));
const PersonalServicesLocationsPage = React.lazy(() => import("./components/personalServices/locations/LocationsPage").then(m => ({ default: m.LocationsPage })));
const PersonalServicesLocationPage = React.lazy(() => import("./components/personalServices/locations/LocationPage").then(m => ({ default: m.LocationPage })));
const PersonalServicesMenuPage = React.lazy(() => import("./components/personalServices/menu/MenuPage").then(m => ({ default: m.MenuPage })));
const PersonalServicesMenuServicePage = React.lazy(() => import("./components/personalServices/menu/MenuServicePage").then(m => ({ default: m.MenuServicePage })));
const PersonalServicesRosterScope = React.lazy(() => import("./components/personalServices/staff/PersonalServicesRosterScope").then(m => ({ default: m.PersonalServicesRosterScope })));
const PersonalServicesSettingsPage = React.lazy(() => import("./components/personalServices/settings/PersonalServicesSettingsPage").then(m => ({ default: m.PersonalServicesSettingsPage })));
// PRD 122 Wave 5: standing appointments and Couldn't book.
const StandingAppointmentsPage = React.lazy(() => import("./components/personalServices/standing/StandingAppointmentsPage").then(m => ({ default: m.StandingAppointmentsPage })));
const AppointmentCalendarPage = React.lazy(() => import("./components/personalServices/calendar/AppointmentCalendarPage").then(m => ({ default: m.AppointmentCalendarPage })));
// PRD 126 W1: appointment checkout, its landing and today's takings — dark behind useCancellationPoliciesShown().
const CheckoutPage = React.lazy(() => import("./components/personalServices/checkout/CheckoutPage").then(m => ({ default: m.CheckoutPage })));
const CheckoutIndexPage = React.lazy(() => import("./components/personalServices/checkout/CheckoutIndexPage").then(m => ({ default: m.CheckoutIndexPage })));
const TakingsPage = React.lazy(() => import("./components/personalServices/checkout/TakingsPage").then(m => ({ default: m.TakingsPage })));
// PRD 126 W2: the module's Tips view and a provider's My tips — dark the same way.
const TipsPage = React.lazy(() => import("./components/personalServices/checkout/TipsPage").then(m => ({ default: m.TipsPage })));
const MyTipsPage = React.lazy(() => import("./components/personalServices/checkout/MyTipsPage").then(m => ({ default: m.MyTipsPage })));
const CannabisSetupPage = React.lazy(() => import("./components/business/cannabis/CannabisSetupPage").then(m => ({ default: m.CannabisSetupPage })));
const CannabisSettingsPage = React.lazy(() => import("./components/business/cannabis/CannabisSettingsPage").then(m => ({ default: m.CannabisSettingsPage })));
const CannabisClassificationPage = React.lazy(() => import("./components/business/cannabis/CannabisClassificationPage").then(m => ({ default: m.CannabisClassificationPage })));
const CannabisSchedulePage = React.lazy(() => import("./components/business/cannabis/CannabisSchedulePage").then(m => ({ default: m.CannabisSchedulePage })));
const CannabisActivitiesPage = React.lazy(() => import("./components/business/cannabis/CannabisActivitiesPage").then(m => ({ default: m.CannabisActivitiesPage })));
const CannabisAllocationsPage = React.lazy(() => import("./components/business/cannabis/CannabisAllocationsPage").then(m => ({ default: m.CannabisAllocationsPage })));
const InventoryValuationReport = React.lazy(() => import("./components/inventory/InventoryValuationReport").then(m => ({ default: m.InventoryValuationReport })));
const PropertiesList = React.lazy(() => import("./components/properties/PropertiesList").then(m => ({ default: m.PropertiesList })));
const PropertyDetail = React.lazy(() => import("./components/properties/PropertyDetail").then(m => ({ default: m.PropertyDetail })));
const LeasesList = React.lazy(() => import("./components/leases/LeasesList").then(m => ({ default: m.LeasesList })));
const LeaseDetail = React.lazy(() => import("./components/leases/LeaseDetail").then(m => ({ default: m.LeaseDetail })));
const RenewalPipeline = React.lazy(() => import("./components/leases/RenewalPipeline").then(m => ({ default: m.RenewalPipeline })));
const LetterOfCreditWatch = React.lazy(() => import("./components/leases/LetterOfCreditWatch").then(m => ({ default: m.LetterOfCreditWatch })));
const MaintenanceQueue = React.lazy(() => import("./components/maintenance/MaintenanceQueue").then(m => ({ default: m.MaintenanceQueue })));
const MaintenanceDetail = React.lazy(() => import("./components/maintenance/MaintenanceDetail").then(m => ({ default: m.MaintenanceDetail })));
const ReportsIndex = React.lazy(() => import("./components/reports/ReportsIndex").then(m => ({ default: m.ReportsIndex })));
const ProfitAndLoss = React.lazy(() => import("./components/reports/ProfitAndLoss").then(m => ({ default: m.ProfitAndLoss })));
const BalanceSheet = React.lazy(() => import("./components/reports/BalanceSheet").then(m => ({ default: m.BalanceSheet })));
const ARAgingReport = React.lazy(() => import("./components/reports/ARAgingReport").then(m => ({ default: m.ARAgingReport })));
const APAgingReport = React.lazy(() => import("./components/reports/APAgingReport").then(m => ({ default: m.APAgingReport })));
const LiveCashFlowStatement = React.lazy(() => import("./components/reports/LiveCashFlowStatement").then(m => ({ default: m.LiveCashFlowStatement })));
const LiveRentRoll = React.lazy(() => import("./components/reports/LiveRentRoll").then(m => ({ default: m.LiveRentRoll })));
const LiveSalesByItem = React.lazy(() => import("./components/reports/LiveSalesByItem").then(m => ({ default: m.LiveSalesByItem })));
const CombiningSchedulePage = React.lazy(() => import("./components/reports/combining/CombiningSchedulePage").then(m => ({ default: m.CombiningSchedulePage })));
const LiveUnitCostAllocation = React.lazy(() => import("./components/reports/LiveUnitCostAllocation").then(m => ({ default: m.LiveUnitCostAllocation })));
const LiveRecoveryReconciliation = React.lazy(() => import("./components/reports/LiveRecoveryReconciliation").then(m => ({ default: m.LiveRecoveryReconciliation })));
// PRD 137 Wave 3: P&L by segment, dark behind SEGMENTS_RELEASED with its catalogue tile.
const LiveProfitAndLossBySegment = React.lazy(() => import("./components/reports/LiveProfitAndLossBySegment").then(m => ({ default: m.LiveProfitAndLossBySegment })));
const LiveStatementOfFinancialPosition = React.lazy(() => import("./components/business/funds/LiveStatementOfFinancialPosition").then(m => ({ default: m.LiveStatementOfFinancialPosition })));
const LiveStatementOfActivities = React.lazy(() => import("./components/business/funds/LiveStatementOfActivities").then(m => ({ default: m.LiveStatementOfActivities })));
const FundReleasesPage = React.lazy(() => import("./components/business/funds/FundReleasesPage").then(m => ({ default: m.FundReleasesPage })));
const TipsPayableReport = React.lazy(() => import("./components/reports/TipsPayableReport").then(m => ({ default: m.TipsPayableReport })));
const RetainageReceivableReport = React.lazy(() => import("./components/reports/RetainageReport").then(m => ({ default: m.RetainageReceivableReport })));
const RetainagePayableReport = React.lazy(() => import("./components/reports/RetainageReport").then(m => ({ default: m.RetainagePayableReport })));
const Live1099kReconciliation = React.lazy(() => import("./components/reports/Live1099kReconciliation").then(m => ({ default: m.Live1099kReconciliation })));
const CashFlowClassificationPage = React.lazy(() => import("./components/business/settings/CashFlowClassificationPage").then(m => ({ default: m.CashFlowClassificationPage })));
const ReportSchedulesPage = React.lazy(() => import("./components/business/settings/ReportSchedulesPage").then(m => ({ default: m.ReportSchedulesPage })));
// PRD 137: Settings → Segments, dark behind SEGMENTS_RELEASED with its hub tile.
const SegmentsSettingsPage = React.lazy(() => import("./components/business/segments/SegmentsSettingsPage").then(m => ({ default: m.SegmentsSettingsPage })));
// PRD 83 Wave 2: Settings → Funds. The page and its hub tile draw only when the
// api sends fund_mode (business/funds/fundAvailability).
const FundsSettingsPage = React.lazy(() => import("./components/business/funds/FundsSettingsPage").then(m => ({ default: m.FundsSettingsPage })));
const LedgerImportPage = React.lazy(() => import("./components/business/imports/LedgerImportPage").then(m => ({ default: m.LedgerImportPage })));
const ListImportPage = React.lazy(() => import("./components/business/imports/list/ListImportPage").then(m => ({ default: m.ListImportPage })));
const Filing1099Page = React.lazy(() => import("./components/business/tax/Filing1099Page").then(m => ({ default: m.Filing1099Page })));
const SettlementsPage = React.lazy(() => import("./components/business/settlements/SettlementsPage").then(m => ({ default: m.SettlementsPage })));
const ReceiptsInboxPage = React.lazy(() => import("./components/banking/receipts/ReceiptsInboxPage").then(m => ({ default: m.ReceiptsInboxPage })));
const RecurringTemplatesList = React.lazy(() => import("./components/business/recurring/RecurringTemplatesList").then(m => ({ default: m.RecurringTemplatesList })));
const RecurringTemplateDetail = React.lazy(() => import("./components/business/recurring/RecurringTemplateDetail").then(m => ({ default: m.RecurringTemplateDetail })));
const NewRecurringTemplatePage = React.lazy(() => import("./components/business/recurring/NewRecurringTemplatePage").then(m => ({ default: m.NewRecurringTemplatePage })));
const EditRecurringTemplatePage = React.lazy(() => import("./components/business/recurring/EditRecurringTemplatePage").then(m => ({ default: m.EditRecurringTemplatePage })));
const CreditMemosList = React.lazy(() => import("./components/business/creditMemos/CreditMemosList").then(m => ({ default: m.CreditMemosList })));
const CreditMemoDetail = React.lazy(() => import("./components/business/creditMemos/CreditMemoDetail").then(m => ({ default: m.CreditMemoDetail })));
const NewCreditMemoPage = React.lazy(() => import("./components/business/creditMemos/NewCreditMemoPage").then(m => ({ default: m.NewCreditMemoPage })));
const VendorCreditsList = React.lazy(() => import("./components/business/vendorCredits/VendorCreditsList").then(m => ({ default: m.VendorCreditsList })));
const VendorCreditDetail = React.lazy(() => import("./components/business/vendorCredits/VendorCreditDetail").then(m => ({ default: m.VendorCreditDetail })));
const NewVendorCreditPage = React.lazy(() => import("./components/business/vendorCredits/NewVendorCreditPage").then(m => ({ default: m.NewVendorCreditPage })));

const MOBILE_BREAKPOINT = 768;

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.background.default};
  display: flex;
  padding-top: ${SYSTEM_BANNER_HEIGHT};
  transition: background-color 0.2s ease, color 0.2s ease;
`;

const MainContent = styled.div<{ $sidebarWidth: string; $resizing: boolean }>`
  flex: 1;
  /* A flex item's minimum width is its content's, so one wide table or one
     non-wrapping toolbar stretched the whole app past the viewport — on a
     phone every business page scrolled sideways, and even at 1280px the
     register did. Zero lets pages be as wide as the screen and no wider;
     anything wider scrolls inside its own wrapper. */
  min-width: 0;
  min-height: 100vh;
  position: relative;
  display: flex;
  flex-direction: column;
  margin-left: ${props => props.$sidebarWidth};
  transition: ${props => props.$resizing ? 'none' : 'margin-left 0.25s cubic-bezier(0.4, 0, 0.2, 1)'};
  
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-left: 0;
    padding-top: 60px;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding-top: 0;
  
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding-top: 0;
  }
`;

// App Shell Layout that includes Navigation and Sidebar
// Where "/" lands (PRD 50, then PRD 40).
//
// Three rules, in this order:
//
// 1. A PRACTITIONER OPENS ON THEIR PRACTICE. Someone who belongs to a firm is
//    here to work other people's books, and Accountant HQ is the screen that
//    says which of them is behind. Dave's call, 2026-09-10, asked as a
//    straight choice against "leave the landing alone": it applies to EVERY
//    firm member, including an accountant who also tracks their own spending,
//    and the Practice section in the sidebar is the one-click way back for
//    them.
// 2. A customer who has hidden the personal side opens on their business
//    dashboard instead of a personal one they told us they do not use.
// 3. Everyone else lands exactly where they always did.
//
// THE FIRM QUESTION IS ASKED FIRST AND IS THE ONE THAT CAN BLOCK. Rule 2 costs
// nothing to evaluate for people who never set the preference; rule 1 has no
// such shortcut, because "do you have a practice" is a fact about the account
// that nothing in the login payload carries. The mirror in firmMembershipStore
// is what keeps that to one wait per browser rather than one per sign-in —
// after the first answer, and after activation, it is synchronous.
/**
 * What `/firm` draws (PRD 66 §5.1).
 *
 * The dashboard for an owner or admin; the worklist for everybody else. The
 * api gates the payload to the same two roles, so a staff member who types
 * `/firm` gets the screen they would have got before rather than a 403 — and
 * `replace` keeps it out of their history, so Back still leaves the app.
 */
const FirmDashboardOrWorklist: React.FC = () => {
  const isFirmManager = useIsFirmManager();

  // This one DOES wait, where the landing redirect deliberately does not: this
  // is a destination somebody navigated to, so a moment of loading is ordinary
  // — where drawing the worklist and then replacing it with a dashboard is a
  // visible flip of the wrong screen.
  if (isFirmManager === null) return <LoadingFallback variant="dashboard" />;
  if (!isFirmManager) return <Navigate to="/firm/clients" replace />;
  return (
    <Suspense fallback={<LoadingFallback variant="dashboard" />}>
      <FirmDashboardPage />
    </Suspense>
  );
};

export const LandingRedirect: React.FC = () => {
  const { isPro } = useAuth();
  const belongsToFirm = useBelongsToFirm();
  const isFirmManager = useIsFirmManager();
  const personalHidden = usePersonalSideHidden();
  // Asked only without Pro: with it, the personal side is theirs to land on.
  const inPaidBusiness = useInPaidBusiness(!isPro);
  // G46: without Pro, where someone whose business stopped being paid for
  // lands. Same request as the answer above.
  const { unpaidOwnedBusinessId } = useOwnsBusiness(!isPro);
  const { unpaidReadBusinessId } = useReadsUnpaidBusiness(!isPro);
  // PRD 89 §4.12: someone whose every membership is a field role, in no firm,
  // opens on the business side, where /business sends each role to its own
  // page (My profile for a technician) — with or without Pro: §4.12 makes no
  // exception, and a technician's personal dashboard is not their work. Asked
  // only once they are known to be in no firm (a practitioner never waits on
  // it), on the same one request per sign-in as the answer above.
  const { landing: fieldLanding } = useFieldStaff(belongsToFirm === false);

  // Unknown, so wait: sending a practitioner to /dashboard and bouncing them
  // to /firm/clients a moment later would be two entries in their history and
  // a visible flash of the wrong app.
  if (belongsToFirm === null) return <LoadingFallback variant="dashboard" />;
  if (belongsToFirm) {
    // PRD 66 §5.1, Dave's call 2026-09-18, revising his own 2026-09-10 answer
    // now that there is a dashboard to land on: "an owner opens the practice
    // to see how it is going; a staff member opens it to do the work."
    //
    // AN UNKNOWN ROLE DOES NOT WAIT HERE. The role rides the same session
    // payload and the same mirror as the membership above, so by this line it
    // is settled in every ordinary case — but an API that predates `firm_role`
    // would leave it null forever, and a landing page that spins is worse than
    // a landing page that is one click off. Unknown lands where EVERY role
    // landed before this change, with Dashboard a row away.
    return <Navigate to={isFirmManager ? "/firm" : "/firm/clients"} replace />;
  }

  // Field staff only, Pro or not. Waits for the same reason the practice
  // question does: the personal dashboard and then a bounce is a visible flash.
  if (fieldLanding === undefined) return <LoadingFallback variant="dashboard" />;
  if (fieldLanding) return <Navigate to={fieldLanding} replace />;

  // Without Pro, a member of a paying business opens on its books: that side
  // is theirs free (Dave, 2026-10-01), and the personal dashboard would greet
  // them with a paywall. Waits on the answer for the same reason.
  if (!isPro) {
    if (inPaidBusiness === null) return <LoadingFallback variant="dashboard" />;
    if (inPaidBusiness) return <Navigate to="/business" replace />;
    // Gap G46: nobody pays for their business. The owner opens on the card
    // that pays for it; a clerk or read-only member on the books they can
    // still read. Both named, so a person with several businesses opens the
    // right one. Settled with inPaidBusiness: one request.
    if (unpaidOwnedBusinessId) {
      return <Navigate to={`${BUSINESS_BILLING_PATH}?business=${encodeURIComponent(unpaidOwnedBusinessId)}`} replace />;
    }
    if (unpaidReadBusinessId) {
      return <Navigate to={`/business?business=${encodeURIComponent(unpaidReadBusinessId)}`} replace />;
    }
  }

  // Null means the business question is still resolving, and it is reached
  // ONLY when the preference is set — so nobody who has not asked for this
  // waits a millisecond for it.
  if (personalHidden === null) return <LoadingFallback variant="dashboard" />;

  return <Navigate to={personalHidden ? "/business" : "/dashboard"} replace />;
};

const AppShell: React.FC = () => {
  const { sidebarWidth, isResizing } = useSidebar();
  const { pathname } = useLocation();

  // Franklin reads this to ask for help about the screen the user just left.
  useEffect(() => {
    rememberRoute(pathname);
  }, [pathname]);

  return (
    <AppContainer>
      <SkipLink />
      <WebNavigation />
      <MainContent $sidebarWidth={sidebarWidth} $resizing={isResizing}>
        <PaidUnnamedBusinessBanner />
        <BusinessPreviewAnnouncement />
        <Suspense fallback={<LoadingFallback />}>
          <ContentArea id="main-content" tabIndex={-1}>
            {/* PRD 64 §4 — "the timer lives in the app shell so that starting
                one does not mean navigating away from the work". Above the
                Outlet, so it follows you across every screen: a timer you
                cannot see from the page you are on is a timer that runs all
                night. */}
            <FirmTimerSlot />
            <Outlet />
          </ContentArea>
        </Suspense>
      </MainContent>
    </AppContainer>
  );
};

// Business module layout — wraps all /business/* routes. The
// `business_accounting` feature flag picks the mode:
//   OFF (default) → preview: the sample-data experience, banner and all.
//   ON            → live: real tenants + accounting API via LiveBusinessShell.
const BusinessShell: React.FC = () => {
  const flagEnabled = useBusinessAccountingFlag();

  // Hold rendering until the flag resolves so live users never flash the
  // sample-data preview (and vice versa).
  if (flagEnabled === null) {
    return <LoadingFallback variant="dashboard" />;
  }

  if (!flagEnabled) {
    return (
      <BusinessModeProvider value="preview">
        <BusinessAccountingProvider>
          <PreviewBanner offersBusiness />
          <Outlet />
        </BusinessAccountingProvider>
      </BusinessModeProvider>
    );
  }

  return (
    <BusinessModeProvider value="live">
      <BusinessProvider>
        <LiveBusinessShell />
      </BusinessProvider>
    </BusinessModeProvider>
  );
};

// Wrap createBrowserRouter with Sentry for automatic navigation spans
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

// Router configuration
// Exported so a test can cross-check it against the navigation: a nav entry
// whose path matches no route is a 404 the type system cannot see, and it has
// happened (the categorization-rules entry shipped pointing into the banking
// tree while its route sat under accounting).
export const routes = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    errorElement: <NotFoundPage />,
    children: [
      {
        index: true,
        element: <LandingRedirect />
      },
      {
        path: "dashboard",
        element: (
          <Suspense fallback={
            <LoadingFallback
              variant="dashboard"
              title="Dashboard"
              subtitle="Your financial overview"
            />
          }>
            <EnhancedDashboard />
          </Suspense>
        )
      },
      {
        path: "net-worth",
        element: (
          <Suspense fallback={
            <LoadingFallback
              variant="chart"
              title="Net Worth"
              subtitle="Track your financial progress over time"
            />
          }>
            <NetWorthPage />
          </Suspense>
        )
      },
      {
        path: "investments",
        children: [
          {
            path: "net-worth", // /investments/net-worth
            element: (
              <Suspense fallback={
                <LoadingFallback
                  variant="chart"
                  title="Net Worth"
                  subtitle="Track your financial progress over time"
                />
              }>
                <NetWorthPage />
              </Suspense>
            )
          },
          {
            path: "portfolio", // /investments/portfolio
            element: (
              <Suspense fallback={
                <LoadingFallback
                  variant="chart"
                  title="Investments"
                  subtitle="Track your portfolio performance"
                  breadcrumbs={[
                    { label: "Dashboard", href: "/dashboard" },
                    { label: "Investments", href: "/investments/portfolio" }
                  ]}
                />
              }>
                <InvestmentsPage />
              </Suspense>
            )
          },
          {
            path: "all-holdings",
            element: (
              <Suspense fallback={<LoadingFallback variant="table" />}>
                <AllHoldingsPage />
              </Suspense>
            )
          },
          {
            path: "performance", // /investments/performance
            element: (
              <Suspense fallback={
                <LoadingFallback
                  variant="chart"
                  title="Performance"
                  subtitle="Portfolio value and holdings"
                  breadcrumbs={[
                    { label: "Dashboard", href: "/dashboard" },
                    { label: "Investments", href: "/investments/portfolio" }
                  ]}
                />
              }>
                <PerformancePage />
              </Suspense>
            )
          },
          {
            index: true,
            element: <Navigate to="portfolio" replace />
          }
        ]
      },
      {
        // Where a client answers "is this firm your accountant?" (PRD 70 §4.6).
        //
        // TOP LEVEL, not under a tenant. The person answering may own nothing
        // anywhere — a 1040 client has no business and no membership — so this
        // cannot sit inside a business shell. It is linked from the invitation
        // email and from the banner on Connected Accounts.
        path: "accountant-requests",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <AccountantRequestsPage />
          </Suspense>
        ),
      },
      {
        path: "accounts",
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={
                <LoadingFallback
                  variant="list"
                  title="Connected Accounts"
                  subtitle="Manage your connected banks and institutions"
                />
              }>
                <ConnectedAccounts />
              </Suspense>
            )
          },
          {
            path: "add",
            element: (
              <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                <DemoGate><AddInstitutionPage /></DemoGate>
              </Suspense>
            )
          }
        ]
      },
      {
        path: "transactions",
        element: (
          <Suspense fallback={
            <LoadingFallback
              variant="table"
              title="Transactions"
              subtitle="Monitor and categorize your spending"
            />
          }>
            <Transactions />
          </Suspense>
        )
      },
      {
        path: "spending",
        element: (
          <Suspense fallback={
            <LoadingFallback
              variant="chart"
              title="Spending"
              subtitle="Track your spending against your budget"
            />
          }>
            <SpendingPage />
          </Suspense>
        )
      },
      {
        // Mobile's Budget screen: month strip, spent vs budgeted, per-category
        // budgets you can edit. Used to redirect to /spending.
        path: "budget",
        element: (
          <Suspense fallback={<LoadingFallback variant="chart" title="Budget" />}>
            <BudgetPage />
          </Suspense>
        )
      },
      // Categories & Rules (mobile: Settings → Categories & Rules). These were
      // in-page state inside Settings, and "Manage categories" from the
      // transaction category picker pointed at a route that did not exist.
      {
        path: "settings/categories",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <CategoriesAndRulesPage />
          </Suspense>
        )
      },
      {
        path: "settings/categories/manage",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <ManageCategories />
          </Suspense>
        )
      },
      {
        path: "settings/categories/quick",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <QuickCategoriesSettings />
          </Suspense>
        )
      },
      {
        path: "settings/rules",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <ManageRulesPage />
          </Suspense>
        )
      },
      {
        path: "settings/rules/new",
        element: (
          <Suspense fallback={<LoadingFallback variant="skeleton" />}>
            <EditRulePage />
          </Suspense>
        )
      },
      {
        path: "settings/rules/:id",
        element: (
          <Suspense fallback={<LoadingFallback variant="skeleton" />}>
            <EditRulePage />
          </Suspense>
        )
      },
      {
        // Suggested transfer pairs to confirm or reject — mobile's
        // PendingTransfers screen, reached from the dashboard card.
        path: "transfers/pending",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" title="Pending Transfers" />}>
            <PendingTransfers />
          </Suspense>
        )
      },
      // Accountant HQ (PRD 40). INSIDE the app shell, which reverses the
      // original placement and the comment that justified it.
      //
      // The old reasoning was that "the shell belongs to a PERSONAL/BUSINESS
      // tenant" and that borrowing it "would put a business switcher above a
      // screen that lists other people's businesses". The second half is true
      // of BusinessShell — which is where the switcher actually lives — and
      // not of AppShell, which is a skip link, the sidebar and an Outlet. So
      // the objection applied to a different component than the one these
      // routes were kept out of, and the cost was real: these screens rendered
      // with no navigation at all, so reaching one meant leaving the app and
      // reaching anything else meant the browser's back button.
      //
      // The worklist has always handed off INTO /business/* (see
      // worklistDestination), so the trip out of HQ worked and the trip back
      // did not. Sharing the shell makes it a round trip, and is what lets the
      // Practice section in WebNavigation lead anywhere.
      //
      // /firm/activate stays OUTSIDE, below: it is reached from the invitation
      // page, which is itself outside the shell, it renders its own full-page
      // layout, and the person seeing it has no practice yet — so the section
      // that would justify the sidebar is exactly the one that cannot render.
      {
        // The firm's home screen — the §5.6 worklist, and where "/" sends
        // anyone who belongs to a practice.
        path: "firm/clients",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorklistPage />
          </Suspense>
        ),
      },
      {
        // The cannabis portfolio (PRD 111 §4.13). Reached from the Clients
        // page's link card, which draws only for a firm with cannabis
        // clients; no nav row. Any firm member; staff see their assigned
        // clients only (the api's visible_firm_clients).
        path: "firm/cannabis",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmCannabisPortfolioPage />
          </Suspense>
        ),
      },
      {
        // One client (PRD 70 §5.2) — the screen PRD 63 §5.1 already listed
        // changes to and nobody had built. It does not shadow the coverage
        // route below: the router ranks by specificity, not by order, so the
        // longer path wins whichever way round they are written.
        path: "firm/clients/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmClientPage />
          </Suspense>
        ),
      },
      {
        // The completeness check (PRD 40 §5.8). Per CLIENT rather than per
        // entity, because a workpaper covers an engagement — a three-LLC
        // client gets one document with three sections, not three documents.
        path: "firm/clients/:id/coverage",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmCoveragePage />
          </Suspense>
        ),
      },
      {
        // The work board (PRD 61 §5). Inside AppShell like the rest of HQ, so
        // the Practice section stays on screen while a practitioner moves
        // between "which client" and "what am I doing for them".
        path: "firm/work",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkBoardPage />
          </Suspense>
        ),
      },
      {
        // One job. Note the path shape: the work-type admin screen will be
        // /firm/work-types rather than /firm/work/types, because ":id" would
        // swallow the literal segment and the settings screen would render as
        // a 404'd work item.
        path: "firm/work/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkItemPage />
          </Suspense>
        ),
      },
      {
        // Templates (PRD 61 §4.5). `/firm/work-templates`, NOT
        // `/firm/work/templates`: the board's ":id" route above would swallow
        // the literal segment and this would render as a 404'd work item.
        path: "firm/work-templates",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkTemplatesPage />
          </Suspense>
        ),
      },
      {
        // Work types and their pipelines (PRD 61 §4.2, §4.3) — the path the
        // ":id" comment above predicted. READ is open to every firm role,
        // matching the API, where only the writes carry require_firm_role!;
        // the screen gates its own buttons.
        path: "firm/work-types",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkTypesPage />
          </Suspense>
        ),
      },
      {
        // The portfolio close board (PRD 62 §4.8) — every client's real state
        // for one period, read from their books rather than from a checklist.
        path: "firm/close",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmCloseBoardPage />
          </Suspense>
        ),
      },
      {
        // The screen staff actually live in (PRD 61 §5).
        path: "firm/my-week",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmMyWeekPage />
          </Suspense>
        ),
      },
      {
        // Recurring work (PRD 61 §4.6). Its own screen rather than a tab on
        // templates: a firm looking for "why has next month's close not
        // appeared" is asking about the schedule, not about the template.
        path: "firm/work-schedules",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkSchedulesPage />
          </Suspense>
        ),
      },
      {
        path: "firm/work-templates/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkTemplateEditorPage />
          </Suspense>
        ),
      },
      {
        // What the client's accountant is waiting on them for (PRD 63 §5.2).
        //
        // NOT UNDER /business, and that is the point. A request is addressed to
        // a PERSON: an individual client has a business or a PFA and may never
        // open either (Dave, 2026-09-17), so a screen that needed a business
        // selected could not show them what their accountant had asked for at
        // all. It has a permanent nav row of its own now — the dashboard card
        // hides itself at zero, which took the screen and the client's whole
        // history with it the moment they finished answering.
        path: "requests",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ClientRequestsPage />
          </Suspense>
        ),
      },
      {
        // Where it used to live. Kept as a redirect rather than deleted: the
        // dashboard card, notification mail and anything a client bookmarked
        // all point here.
        path: "business/requests",
        element: <Navigate to="/requests" replace />,
      },
      {
        // The weekly timesheet (PRD 64 §4). The primary place time is entered —
        // §3.3: a product that only has a timer is a product whose time data is
        // invented on Fridays anyway.
        path: "firm/time",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmTimePage />
          </Suspense>
        ),
      },
      {
        // Teams (PRD 66 §4.5). Practice Admin rather than the rail: a team is
        // set up once and then read about, like Rates and Staff.
        path: "firm/teams",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmTeamsPage />
          </Suspense>
        ),
      },
      {
        // Work throughput (PRD 66 §4.2).
        path: "firm/insights/work",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWorkInsightsPage />
          </Suspense>
        ),
      },
      {
        // Utilisation (PRD 66 §4.2) — both ratios, the second blank rather
        // than invented for anybody whose week nobody has stated.
        path: "firm/insights/utilisation",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmUtilisationPage />
          </Suspense>
        ),
      },
      {
        // Portfolio book health (PRD 66 §4.3) — the report no competitor can
        // draw, because it reads the clients' own ledgers.
        path: "firm/insights/book-health",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmBookHealthPage />
          </Suspense>
        ),
      },
      {
        // PRD 64 §3.6 — a forecast and an observed backlog, kept apart.
        path: "firm/capacity",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmCapacityPage />
          </Suspense>
        ),
      },
      {
        // Practice billing — what the firm charges ITS OWN clients (PRD 65 §5).
        //
        // §5's table writes these as `/firm/billing/*`. THAT PREFIX IS TAKEN by
        // PRD 40 §5.4.2's subscription screen — the other direction of the same
        // word — and it is not moved, because `/firm/billing?checkout=done` is
        // the `successUrl` handed to STRIPE and is baked into checkout sessions
        // that already exist. A firm coming back from paying us would land on a
        // screen about billing its own clients. A route string in a PRD is
        // cheaper to bend than a payment return.
        path: "firm/client-billing",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmClientBillingPage />
          </Suspense>
        ),
      },
      {
        path: "firm/client-billing/arrangements",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmArrangementsPage />
          </Suspense>
        ),
      },
      {
        path: "firm/client-billing/wip",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmWipPage />
          </Suspense>
        ),
      },
      {
        path: "firm/client-billing/realization",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmRealizationPage />
          </Suspense>
        ),
      },
      {
        // PRD 65 §4.6 — signing is what starts the billing.
        path: "firm/letters",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmLettersPage />
          </Suspense>
        ),
      },
      {
        // PRD 64 §3.4. Admin, and reached from the Practice Admin hub rather
        // than the rail: §4 puts Time and Capacity in the sidebar and leaves
        // this with the practice's other administration.
        path: "firm/rates",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmRatesPage />
          </Suspense>
        ),
      },
      {
        // Client messaging (PRD 68 §5.1). The list defaults to "waiting on us",
        // oldest first — the queue is about how long somebody has waited.
        path: "firm/messages",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmMessagesPage />
          </Suspense>
        ),
      },
      {
        path: "firm/messages/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmMessageThreadPage />
          </Suspense>
        ),
      },
      {
        // Client requests (PRD 63 §5.1). `/firm/requests/new` is declared BEFORE
        // `/firm/requests/:id` so the literal segment is not swallowed by the
        // parameter — the same trap `/firm/work` hit with `work-templates`.
        path: "firm/requests",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmRequestsPage />
          </Suspense>
        ),
      },
      {
        path: "firm/requests/new",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmRequestComposerPage />
          </Suspense>
        ),
      },
      {
        path: "firm/requests/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmRequestDetailPage />
          </Suspense>
        ),
      },
      {
        // Prospects — the sales pipeline (PRD 69 §5). Inside AppShell like the
        // rest of HQ; a won prospect hands off to /firm/clients, so the
        // Practice section has to stay on screen across the two.
        path: "firm/prospects",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmProspectsPage />
          </Suspense>
        ),
      },
      {
        // PRD 72 §5 — the practice's outreach settings.
        path: "firm/outreach",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmOutreachPage />
          </Suspense>
        ),
      },
      {
        // PRD 74 §4.4 — the practice's pipeline numbers, above `:id`.
        path: "firm/prospects/analytics",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmProspectAnalyticsPage />
          </Suspense>
        ),
      },
      {
        // PRD 73 §4.2 — above `:id` so the literal is not swallowed as one.
        path: "firm/prospects/import",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmProspectImportPage />
          </Suspense>
        ),
      },
      {
        path: "firm/prospects/:id",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmProspectPage />
          </Suspense>
        ),
      },
      {
        // Practice Admin (PRD 40 §5.2): one sidebar row in front of the three
        // screens that run the practice itself, rather than three rows beside
        // the ones a practitioner opens every morning.
        path: "firm/admin",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmAdminPage />
          </Suspense>
        ),
      },
      {
        // The practice's own details. The ROUTE is open to the firm — an admin
        // sent the link reads it — while the write is the owner's and the
        // server enforces that. A route that 403'd on GET would make a shared
        // link look broken.
        path: "firm/admin/info",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmInfoPage />
          </Suspense>
        ),
      },
      {
        // The practice's roster, and the §3.2 evidence beside the button that
        // produces it — an artefact in a separate audit section is one nobody
        // finds when an examiner asks.
        path: "firm/staff",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmStaffPage />
          </Suspense>
        ),
      },
      {
        // Where the billing banner has been sending people (PRD 40 §5.4.2).
        // It links here from every firm screen, so this route existing is what
        // makes that banner's "update card" more than a dead end.
        path: "firm/billing",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <DemoGate><FirmBillingPage /></DemoGate>
          </Suspense>
        ),
      },
      {
        // "Where the forty stand" (PRD 40 §5.7.1). Its own screen rather than
        // a worklist column, because a migration is a finite project a firm
        // works through once and then never looks at again — and while it is
        // running it is the only thing they want to see.
        path: "firm/migrations",
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FirmMigrationBoardPage />
          </Suspense>
        ),
      },
      {
        // Bare /firm is THE DASHBOARD (PRD 66 §4.2) for the roles that can see
        // it, and still the worklist for everyone else — the api gates the
        // payload to owner and admin, so a staff member who types this in gets
        // the screen they would have got before rather than a 403.
        path: "firm",
        element: <FirmDashboardOrWorklist />,
      },
      // Business module routes (preview — sample data only)
      {
        path: "business",
        element: <BusinessShell />,
        children: [
          {
            // The dashboard is closed to field roles (PRD 89 §4.12): they are
            // sent to their own page instead of a refusal.
            index: true,
            element: (
              <BusinessIndexRoute>
                <Suspense fallback={<LoadingFallback variant="dashboard" />}>
                  <BusinessDashboard />
                </Suspense>
              </BusinessIndexRoute>
            )
          },
          {
            path: "settings",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <BusinessSettingsPage />
                  </Suspense>
                )
              },
              {
                path: "branding",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <InvoiceBrandingPage />
                  </Suspense>
                )
              },
              {
                path: "numbering",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <DocumentNumberingPage />
                  </Suspense>
                )
              },
              {
                // PRD 101. Under /business/settings, which LIVE_ROUTE_PREFIXES
                // already covers, so it needs no prefix of its own.
                path: "sales-tax",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <SalesTaxSettingsPage />
                  </Suspense>
                )
              },
              {
                path: "profile",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <BusinessProfilePage />
                  </Suspense>
                )
              },
              {
                path: "businesses",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <BusinessesPage />
                  </Suspense>
                )
              },
              {
                path: "members",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <MembersPage />
                  </Suspense>
                )
              },
              {
                // The client's half of PRD 40 §5.5. Beside members because
                // both answer "who can see this business" — and until now the
                // API served this and nothing in web called it, so a practice
                // could ask and the client had no way to answer.
                path: "accountant",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <YourAccountantPage />
                  </Suspense>
                )
              },
              {
                path: "billing",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <DemoGate><BillingPage /></DemoGate>
                  </Suspense>
                )
              },
              {
                // PRD 72 §5 — outreach on the CRM module.
                path: "outreach",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <BusinessOutreachSettingsPage />
                  </Suspense>
                )
              },
              {
                path: "books",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <BooksPage />
                  </Suspense>
                )
              },
              {
                path: "aging-buckets",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <AgingBucketsPage />
                  </Suspense>
                )
              },
              {
                path: "late-fees",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <LateFeesPage />
                  </Suspense>
                )
              },
              {
                // PRD 125 §5: the cancellation policy — core, dark with
                // personal services until it is released (the page sends
                // anyone else back to Administration). A child of
                // /business/settings, so it inherits the live-route prefix.
                path: "cancellation-policy",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <CancellationPolicyPage />
                  </Suspense>
                )
              },
              {
                // The third-party app directory, and now the only tile on the
                // Administration hub that leads to Stripe setup. A child of
                // /business/settings, so it inherits the live-route prefix and
                // does not draw the sample-data banner over a real connection.
                path: "marketplace",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <MarketplacePage />
                  </Suspense>
                )
              },
              {
                // PRD 56 §9. The Stripe App install returns here, with the
                // outcome in the query string (?stripe=connected and so on).
                // Reached from the marketplace card rather than from a hub
                // tile of its own, and still by URL from Stripe's callback.
                path: "payments",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <PaymentsPage />
                  </Suspense>
                )
              },
              {
                path: "accounting-basis",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <AccountingBasisPage />
                  </Suspense>
                )
              },
              {
                path: "auto-categorization",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <AutoCategorizationPage />
                  </Suspense>
                )
              },
              {
                // Its own page rather than a settings tab: it is a table of
                // every GL account, and the cash flow statement links here
                // directly when an unclassified account breaks reconciliation
                path: "cash-flow-classification",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <CashFlowClassificationPage />
                  </Suspense>
                )
              },
              {
                // The read side of the audit trail (PRD 33). Owner and
                // accountant only — the hub hides the tile for everyone else,
                // and the page itself says so if reached directly.
                path: "activity",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <ActivityLogAdminPage />
                  </Suspense>
                )
              },
              {
                // PRD 25 §5.6: every bulk action, and what became of each
                // record in one. Every member reads it. The completion
                // notification opens the one it is about.
                path: "bulk-operations",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <BulkOperationsPage />
                  </Suspense>
                )
              },
              {
                path: "bulk-operations/:operationId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <BulkOperationDetailPage />
                  </Suspense>
                )
              },
              {
                // PRD 18 §8: outbound webhooks — owner and accountant.
                path: "webhooks",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <WebhooksPage />
                  </Suspense>
                )
              },
              {
                path: "report-schedules",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <ReportSchedulesPage />
                  </Suspense>
                )
              },
              // PRD 83 Wave 2: funds. Always routed — funds are core — and the
              // page itself says so when this business's framework has none.
              // Under /business/settings, so LIVE_ROUTE_PREFIXES covers it.
              {
                path: "funds",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <FundsSettingsPage />
                  </Suspense>
                )
              },
              // PRD 137 Wave 1: class, department and location tags. Out until
              // the forecasting module is released (segments/release.ts). Under
              // /business/settings, so LIVE_ROUTE_PREFIXES already covers it.
              ...(SEGMENTS_RELEASED
                ? [
                    {
                      path: "segments",
                      element: (
                        <Suspense fallback={<LoadingFallback variant="list" />}>
                          <SegmentsSettingsPage />
                        </Suspense>
                      )
                    }
                  ]
                : []),
              {
                // Migration from QuickBooks (PRD 32) or Xero (PRD 38). Under
                // settings rather than onboarding because it is not only a
                // first-run action — a business can bring a second year across
                // later. One route for both: the source is a field on the
                // form, not a different screen.
                path: "import",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <LedgerImportPage />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: "banking",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <BankingAccountsList />
                  </Suspense>
                )
              },
              {
                // PRD 21: receipts read and matched to bank transactions. In the
                // banking tree because a receipt is evidence for one, and so
                // covered by the '/business/banking' live prefix and its nav row.
                path: "receipts",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <ReceiptsInboxPage />
                  </Suspense>
                )
              },
              {
                // Processor payouts and the Deposits in Transit tie-out (PRD
                // 57). In the banking tree because a payout is money landing
                // in a bank account, and the clearing account it passes
                // through is reconciled the same way a bank account is.
                //
                // Static "payouts" cannot collide with ":bankAccountId" —
                // that segment is only ever followed by another one.
                path: "payouts",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <SettlementsPage />
                      </Suspense>
                    )
                  },
                  {
                    // The same page with one payout's drawer open. A route
                    // rather than component state, so a bank-register row can
                    // link straight to the payout behind a deposit (PRD 57 §1)
                    // — including a POSTED one, which the worklist above
                    // deliberately does not carry.
                    path: ":settlementId",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <SettlementsPage />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                // The reconciliation LANDING page — one level above the
                // per-account screen below. Static "reconcile" cannot collide
                // with ":bankAccountId/reconcile" (one segment vs two), and
                // keeping it in the banking tree means reconciliation has one
                // tree, not two.
                path: "reconcile",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <ReconciliationOverview />
                  </Suspense>
                )
              },
              {
                // Live mode only — the components redirect in preview mode.
                // Rules live in the BANKING tree because they act on the bank
                // feed; the nav lists them under Accounting, the same way
                // Reconcile above does. Nav placement and URL need not agree.
                path: "rules",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="list" />}>
                        <RulesPage />
                      </Suspense>
                    )
                  },
                  {
                    // Before ":id", or "new" resolves as a rule id.
                    path: "new",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <RuleBuilderPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <RuleDetailPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id/edit",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <RuleBuilderPage />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                path: ":bankAccountId/register",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <BankRegister />
                  </Suspense>
                )
              },
              {
                // PRD 02 §10 sketches /settings/banking/:id/reconcile, but the
                // shipped app puts the register at /business/banking/:id/register
                // — reconciliation sits beside it rather than in a second tree.
                path: ":bankAccountId/reconcile",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <ReconcilePage />
                  </Suspense>
                )
              },
              {
                // PRD 16 §5.7 history: every session, undone and abandoned
                // included, and a read-only view of one.
                path: ":bankAccountId/reconciliations",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <ReconciliationHistoryPage />
                  </Suspense>
                )
              },
              {
                path: ":bankAccountId/reconciliations/:reconciliationId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <ReconciliationHistoryDetailPage />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: "accounting",
            children: [
              {
                // Approved expense reports from a connected expense tool,
                // mapped and posted (spend-expensify.md §3.4).
                path: "expense-reports",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <ExpenseReportsPage />
                  </Suspense>
                )
              },
              {
                // A connected storefront's days, posted as one entry each
                // (commerce TDD §4).
                path: "store-sales",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <StoreSalesPage />
                  </Suspense>
                )
              },
              {
                path: "chart-of-accounts",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <ChartOfAccounts />
                  </Suspense>
                )
              },
              {
                path: "accounts/:glAccountId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <GLAccountLedger />
                  </Suspense>
                )
              },
              {
                path: "journal-entries",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <JournalEntriesList />
                      </Suspense>
                    )
                  },
                  {
                    // Live mode only — the component redirects in preview mode
                    path: "new",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <JournalEntryForm />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <JournalEntryDetail />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                // The fixed-asset register (PRD 52). Under Accounting rather
                // than Properties, because §2 is an argument that this is
                // CORE — a bakery's oven depreciates. `property_id` is a
                // dimension on an asset, not a dependency of one.
                //
                // "tax-report" is declared BEFORE ":assetId", or a request for
                // it is matched as an asset id.
                path: "fixed-assets",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <FixedAssetsList />
                      </Suspense>
                    )
                  },
                  {
                    path: "tax-report",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <TaxDepreciationReportPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":assetId",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <FixedAssetDetail />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                // Live mode only — the component redirects in preview mode
                path: "period-close",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <PeriodClosePage />
                  </Suspense>
                )
              },
              {
                // The month-end close WORKFLOW (PRD 17), beside the lock's own
                // screen above. Live mode only — the component redirects in
                // preview mode, which has no ledger to close.
                path: "month-end-close",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <MonthEndClosePage />
                  </Suspense>
                )
              },
              {
                // Recurring journal entries (PRD 17 §5.4). Reached from the
                // journal and from the close checklist's recurring items, so
                // it needs no nav row of its own. Live mode only — the page
                // redirects in preview mode.
                path: "recurring-entries",
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <RecurringJournalEntriesPage />
                  </Suspense>
                )
              },
              {
                // Live mode only — the component redirects in preview mode
                path: "trial-balance",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <TrialBalance />
                  </Suspense>
                )
              }
            ]
          },
          {
            // PRD 101 Wave 3 — what is owed each state and the payments made.
            // Under /business/tax, which LIVE_ROUTE_PREFIXES already covers.
            path: "tax/sales",
            element: (
              <Suspense fallback={<LoadingFallback variant="list" />}>
                <SalesTaxPage />
              </Suspense>
            )
          },
          {
            // PRD 119 §5.2 — Where you owe: the nexus dashboard and its map.
            // Its own chunk, so the map's data loads only here.
            path: "tax/sales/nexus",
            element: (
              <Suspense fallback={<LoadingFallback variant="list" />}>
                <NexusDashboardPage />
              </Suspense>
            )
          },
          {
            // PRD 119 §5.3 — Returns: each state's return, prepared from the
            // liability report, filed on the state's portal.
            path: "tax/sales/returns",
            element: (
              <Suspense fallback={<LoadingFallback variant="list" />}>
                <ReturnsPage />
              </Suspense>
            )
          },
          {
            // 1099 filing (PRD 13 §6.1). The year is optional: January's
            // default is the year just ended, which the page works out.
            path: "tax/1099",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <Filing1099Page />
                  </Suspense>
                )
              },
              {
                path: ":taxYear",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <Filing1099Page />
                  </Suspense>
                )
              }
            ]
          },
          {
            // PRD 125 §5: Sales → Cancellation fees — core, dark with
            // personal services until it is released (the page sends anyone
            // else back to /business). `?tab=` picks the queue, `?fee=` opens
            // a fee's drawer (the appointment drawer and the notifications
            // link there).
            path: "cancellation-fees",
            element: (
              <Suspense fallback={<LoadingFallback variant="table" />}>
                <CancellationFeesPage />
              </Suspense>
            )
          },
          {
            path: "credit-memos",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <CreditMemosList />
                  </Suspense>
                )
              },
              {
                // Before :id, or "new" is read as a credit memo id
                path: "new",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <NewCreditMemoPage />
                  </Suspense>
                )
              },
              {
                path: ":id",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <CreditMemoDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            // The payable mirror (PRD 34). Its own noun, not "credit memos":
            // one is a document you send a customer, the other one you
            // receive, and sharing the route would make the list ambiguous
            // about which direction the money runs.
            path: "vendor-credits",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <VendorCreditsList />
                  </Suspense>
                )
              },
              {
                // Before :id, or "new" is read as a vendor credit id
                path: "new",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <NewVendorCreditPage />
                  </Suspense>
                )
              },
              {
                path: ":id",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <VendorCreditDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            // The CRM module (PRD 69 §5): the same board Accountant HQ has at
            // /firm/prospects, on the business seam, where a won prospect
            // becomes a customer. `crm/:id` after `crm` so the literal is not
            // swallowed as an id.
            path: "crm",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BusinessProspectsPage />
              </Suspense>
            )
          },
          {
            // PRD 74 — above `crm/:id` so the literal is not swallowed.
            path: "crm/analytics",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BusinessProspectAnalyticsPage />
              </Suspense>
            ),
          },
          {
            // PRD 73 §4.2 — above `crm/:id` so the literal is not swallowed.
            path: "crm/import",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BusinessProspectImportPage />
              </Suspense>
            ),
          },
          {
            path: "crm/:id",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <BusinessProspectPage />
              </Suspense>
            )
          },
          {
            // Field service (PRD 88 §5.2). The Overview is the module's front
            // door and carries the banner for a business without it; Settings
            // is reached from it and from Administration. Later PRDs add their
            // pages under this subtree, after the literals, before any `:id`.
            // PRD 95 §4.13: FieldShell is every field page's parent — the
            // installable page's manifest, the scoped service worker and its
            // update prompt.
            path: "field",
            element: <FieldShell />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldServiceOverviewPage />
                  </Suspense>
                )
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldServiceSettingsPage />
                  </Suspense>
                )
              },
              // PRD 109 §5 — switching in from Housecall Pro, Jobber or
              // ServiceTitan: the guide, and the import for each kind (the
              // core list import page, given the field kind).
              {
                path: "switch",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldSwitchGuidePage />
                  </Suspense>
                )
              },
              // PRD 109 Wave 3 — the worklists a jobs import leaves.
              {
                path: "switch/recurring",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <RecurringWorklistPage />
                  </Suspense>
                )
              },
              {
                path: "switch/deposits",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <DepositsWorklistPage />
                  </Suspense>
                )
              },
              {
                path: "import/:kind",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <FieldImportPage />
                  </Suspense>
                )
              },
              // PRD 88 §5.2 — service locations. A row opens the location's
              // page, where it is pinned, edited and given units and contacts.
              {
                path: "locations",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <ServiceLocationsList />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <LocationPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 90 §5 — jobs. A row opens the job's page: its visits, the
              // schedule drawer, Record status, files and the status log.
              {
                path: "jobs",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <JobsList />
                      </Suspense>
                    )
                  },
                  {
                    path: ":jobId",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <JobPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 90 Wave 3 — recurring jobs. A row opens its page: the
              // rule, the next six dates with their jobs, and the jobs it made.
              {
                path: "recurring",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <RecurringList />
                      </Suspense>
                    )
                  },
                  {
                    path: ":recurrenceId",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <RecurrencePage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 98 §5 — estimates. A row opens the estimate's page: the
              // builder while a draft, then what was sent and what was approved.
              {
                path: "estimates",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <EstimatesList />
                      </Suspense>
                    )
                  },
                  {
                    path: ":estimateId",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <FieldEstimatePage />
                      </Suspense>
                    )
                  },
                  // PRD 98 Wave 2 — present mode: the customer's view full-screen
                  // on this device, where she signs.
                  {
                    path: ":estimateId/present",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <FieldEstimatePresentPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 110 §5 — install projects. A row opens the project's page.
              {
                path: "projects",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <ProjectsList />
                      </Suspense>
                    )
                  },
                  {
                    path: ":projectId",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <ProjectPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 102 §5 — memberships. A row opens the membership's page;
              // present mode hands this device to the customer to sign.
              {
                path: "memberships",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <MembershipsList />
                      </Suspense>
                    )
                  },
                  {
                    path: ":membershipId",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <MembershipPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":membershipId/present",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <PresentMembershipPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 91 §5.1 — the day board.
              {
                path: "dispatch",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <DispatchBoardPage />
                  </Suspense>
                )
              },
              // PRD 91 §5.4 — a technician's printable route sheet (Wave 3).
              {
                path: "dispatch/route-sheet",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <FieldRouteSheetPage />
                  </Suspense>
                )
              },
              // PRD 91 §5.3 — the week and month calendars (Wave 2).
              {
                path: "calendar",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <FieldCalendarPage />
                  </Suspense>
                )
              },
              // PRD 100 §5 — the billing queue for jobs. A row opens the job,
              // where Create invoice lives.
              {
                path: "invoicing",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <InvoicingPage />
                  </Suspense>
                )
              },
              // PRD 100 Wave 4 — the Job profitability report.
              {
                path: "reports/jobs",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <JobProfitabilityPage />
                  </Suspense>
                )
              },
              // PRD 99 Wave 3 — Payments: field collections and tips.
              {
                path: "payments",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <FieldPaymentsPage />
                  </Suspense>
                )
              },
              // PRD 97 §5 — the price book: the office's list of tasks, labour
              // rates, fees and parts, its categories, markup and re-price.
              {
                path: "price-book",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <PriceBookPage />
                      </Suspense>
                    )
                  },
                  // Wave 4 — the price book's import, on the core list import page.
                  {
                    path: "import",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <PriceBookImportPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 94 §5 — the customer responses queue: open asks for a
              // different time, oldest first. The destination of
              // field_reschedule_requested.
              {
                path: "responses",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CustomerResponsesPage />
                  </Suspense>
                )
              },
              // PRD 104 §5 — customer feedback: responses, recovery, asks and
              // the report; and its settings tab.
              {
                path: "feedback",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FeedbackScreen />
                  </Suspense>
                )
              },
              {
                path: "recommendations",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <RecommendationsScreen />
                  </Suspense>
                )
              },
              {
                path: "settings/feedback",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FeedbackSettingsPage />
                  </Suspense>
                )
              },
              // PRD 92 §5 — Book a job: the call-booking screen (and, with
              // ?request=, the same screen filing a request), and the
              // Requests inbox it files from.
              {
                path: "book",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CallBookingPage />
                  </Suspense>
                )
              },
              {
                path: "requests",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <BookingRequestsPage />
                  </Suspense>
                )
              },
              // PRD 95 §5 — the technician's day: My day, a visit's screen,
              // My time, and the office's day of time.
              {
                path: "my-day",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyDayPage />
                  </Suspense>
                )
              },
              {
                path: "my-time",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyTimePage />
                  </Suspense>
                )
              },
              {
                path: "time",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldTimePage />
                  </Suspense>
                )
              },
              // PRD 105 §5 — timesheets, the payroll export and pay settings.
              {
                path: "timesheets",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <TimesheetsPage />
                  </Suspense>
                )
              },
              {
                path: "timesheets/exports",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PayrollExportsPage />
                  </Suspense>
                )
              },
              // PRD 105 Waves 3–4 — commissions, plans, performance and My numbers.
              {
                path: "commissions",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CommissionsPage />
                  </Suspense>
                )
              },
              {
                path: "commissions/plans",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CommissionPlansPage />
                  </Suspense>
                )
              },
              {
                path: "commissions/plans/:planId",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CommissionPlanPage />
                  </Suspense>
                )
              },
              {
                path: "performance",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldPerformancePage />
                  </Suspense>
                )
              },
              {
                path: "performance/technicians/:technicianId",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ScorecardPage />
                  </Suspense>
                )
              },
              {
                path: "my-numbers",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyNumbersPage />
                  </Suspense>
                )
              },
              {
                path: "settings/pay",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PaySettingsPage />
                  </Suspense>
                )
              },
              // PRD 95 Wave 3 — the signing screen, and the business's own disclosures.
              {
                path: "visits/:visitId/sign/:kind",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <SignVisitPage />
                  </Suspense>
                )
              },
              // PRD 94 §5 — Customer notifications: one card per notice rule.
              {
                path: "settings/notifications",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <NotificationRulesPage />
                  </Suspense>
                )
              },
              {
                path: "settings/signatures",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <SignatureDisclosuresPage />
                  </Suspense>
                )
              },
              {
                path: "settings/checklists",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ChecklistSettingsPage />
                  </Suspense>
                )
              },
              {
                path: "settings/checklists/:templateId",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ChecklistTemplatePage />
                  </Suspense>
                )
              },
              {
                path: "checklist-results",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ChecklistResultsPage />
                  </Suspense>
                )
              },
              {
                path: "visits/:visitId/checklists/:checklistId",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ChecklistRunnerPage />
                  </Suspense>
                )
              },
              {
                path: "visits/:visitId",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldVisitPage />
                  </Suspense>
                )
              },
              // PRD 89 §5 — the roster. A row opens the technician's page.
              {
                path: "technicians",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <TechniciansPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <TechnicianPage />
                      </Suspense>
                    )
                  },
                  // PRD 96 §5: a technician's location on one day, after a reason.
                  {
                    path: ":id/location",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <TechnicianLocationHistoryPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 106 §5: equipment, a unit's page, permits, and their settings.
              {
                path: "equipment",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <EquipmentListPage />
                  </Suspense>
                )
              },
              {
                path: "equipment/:id",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <EquipmentPage />
                  </Suspense>
                )
              },
              {
                path: "permits",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PermitsPage />
                  </Suspense>
                )
              },
              {
                path: "settings/equipment-and-permits",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <EquipmentSettingsPage />
                  </Suspense>
                )
              },
              // PRD 103 §5: truck stock.
              {
                path: "stock",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <StockPage />
                  </Suspense>
                )
              },
              {
                path: "stock/locations/:id",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <StockLocationPage />
                  </Suspense>
                )
              },
              {
                path: "stock/counts/:id",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <StockCountPage />
                  </Suspense>
                )
              },
              {
                path: "stock/replenishment",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ReplenishmentPage />
                  </Suspense>
                )
              },
              {
                path: "purchasing",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PurchasingPage />
                  </Suspense>
                )
              },
              {
                path: "purchasing/:id",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PurchaseOrderPage />
                  </Suspense>
                )
              },
              {
                path: "stock/transfers",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <StockTransfersPage />
                  </Suspense>
                )
              },
              {
                path: "my-van",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyVanPage />
                  </Suspense>
                )
              },
              {
                path: "settings/stock",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <StockSettingsPage />
                  </Suspense>
                )
              },
              // PRD 96 Wave 2 §5: the mileage log, its report and its settings.
              {
                path: "mileage",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MileagePage />
                  </Suspense>
                )
              },
              {
                path: "mileage/log",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MileageLogPage />
                  </Suspense>
                )
              },
              {
                path: "settings/mileage",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MileageSettingsPage />
                  </Suspense>
                )
              },
              // PRD 96 §5: My location — the notice, my choice, who looked.
              {
                path: "my-location",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyLocationPage />
                  </Suspense>
                )
              },
              // §5 (Wave 3): My profile — the caller's own roster row.
              {
                path: "me",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyProfilePage />
                  </Suspense>
                )
              },
              // §5: everyone's time off, and the requests to decide.
              {
                path: "time-off",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <TimeOffPage />
                  </Suspense>
                )
              },
              // §5: the vans. `:id` opens one in the page's drawer (audit links,
              // the technician page).
              {
                path: "vehicles",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <VehiclesPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <VehiclesPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // §5: the roster's settings, under Settings (more specific than
              // its `settings` leaf, so they win the match).
              {
                path: "settings/skills",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <SkillsSettingsPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <SkillsSettingsPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 96 §5: technician location, under Settings.
              {
                path: "settings/location",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <LocationSettingsPage />
                  </Suspense>
                )
              },
              // PRD 110 §5: project defaults, under Settings.
              {
                path: "settings/projects",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ProjectSettingsPage />
                  </Suspense>
                )
              },
              // PRD 102 §5: membership plans, under Settings.
              {
                path: "settings/membership-plans",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MembershipPlansPage />
                  </Suspense>
                )
              },
              // PRD 98 §5: estimate settings and templates, under Settings.
              {
                path: "settings/estimates",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FieldEstimateSettingsPage />
                  </Suspense>
                )
              },
              // PRD 99 Wave 4: the vans' card readers, under Settings.
              {
                path: "settings/readers",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CardReadersSettingsPage />
                  </Suspense>
                )
              },
              // PRD 99 Wave 5: the business's own lenders and their agreements, under Settings.
              {
                path: "settings/financing",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <FinancingSettingsPage />
                  </Suspense>
                )
              },
              // PRD 107 §5: Customer account settings, under Settings.
              {
                path: "settings/hub",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <HubSettingsPage />
                  </Suspense>
                )
              },
              // PRD 92 §5: Online booking, under Settings — the pages, and one
              // page's editor.
              {
                path: "settings/booking",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <BookingPagesSettingsPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <BookingPageEditor />
                      </Suspense>
                    )
                  },
                ]
              },
              // PRD 90 §5: job types, under Settings; `:id` opens one in the drawer.
              {
                path: "settings/job-types",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <JobTypesSettingsPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <JobTypesSettingsPage />
                      </Suspense>
                    )
                  },
                ]
              },
              {
                path: "settings/zones",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <ZonesSettingsPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <ZonesSettingsPage />
                      </Suspense>
                    )
                  },
                ]
              },
            ]
          },
          {
            path: "customers",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <CustomersList />
                  </Suspense>
                )
              },
              // PRD 26, reached from the list. Before :id, or "import" is read
              // as a customer id. Live only: the page redirects in preview. Out
              // until the api it needs is on prod (listImportsLive).
              ...(LIST_IMPORTS_LIVE
                ? [
                    {
                      path: "import",
                      element: (
                        <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                          <ListImportPage target="customers" />
                        </Suspense>
                      )
                    }
                  ]
                : []),
              {
                // PRD 99 §4.3: a deposit notification that names only the
                // deposit opens here, and goes on to its customer's page with
                // the deposit open. Before :id, or "deposits" is read as one.
                path: "deposits/:depositId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <CustomerDepositRedirect />
                  </Suspense>
                )
              },
              {
                // Live mode only (PRD 35 §2) — the component redirects in preview mode
                path: ":id",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <CustomerDetailPage />
                  </Suspense>
                )
              }
            ]
          },
          {
            // Messages from your accountant (PRD 68 §5.2). Business-scoped
            // because the conversation is about a set of books.
            path: "messages",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ClientMessagesPage />
              </Suspense>
            )
          },
          {
            path: "messages/:id",
            element: (
              <Suspense fallback={<LoadingFallback />}>
                <ClientMessageThreadPage />
              </Suspense>
            )
          },
          {
            path: "invoices",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <InvoicesList />
                  </Suspense>
                )
              },
              {
                // Live mode only — the component redirects in preview mode
                path: "new",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <NewInvoicePage />
                  </Suspense>
                )
              },
              {
                // Before :id, or "recurring" is read as an invoice id
                path: "recurring",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <RecurringTemplatesList />
                      </Suspense>
                    )
                  },
                  {
                    // Live mode only — the component redirects in preview mode.
                    // Before :id, or "new" is read as a template id and the
                    // detail page asks the API for a template called "new".
                    path: "new",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <NewRecurringTemplatePage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <RecurringTemplateDetail />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id/edit",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                        <EditRecurringTemplatePage />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                // Live mode only — the Make Deposit tool (PRD 05 §3.8)
                path: "undeposited-funds",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <UndepositedFundsPage />
                  </Suspense>
                )
              },
              {
                path: ":id",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <InvoiceDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: "vendors",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <VendorsList />
                  </Suspense>
                )
              },
              // PRD 26, reached from the list. Before :id, or "import" is read
              // as a vendor id. Live only: the page redirects in preview. Out
              // until the api it needs is on prod (listImportsLive).
              ...(LIST_IMPORTS_LIVE
                ? [
                    {
                      path: "import",
                      element: (
                        <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                          <ListImportPage target="vendors" />
                        </Suspense>
                      )
                    }
                  ]
                : []),
              {
                // Live mode only (PRD 35 §2) — the component redirects in preview mode
                path: ":id",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <VendorDetailPage />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: "bills",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <BillsList />
                  </Suspense>
                )
              },
              {
                // Live mode only — the component redirects in preview mode
                path: "new",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <NewBillPage />
                  </Suspense>
                )
              },
              {
                path: ":id",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <BillDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: "inventory",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <InventoryItemsList />
                  </Suspense>
                )
              },
              {
                path: ":itemId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <InventoryItemDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            // Point of sale (PRD 118): the register, and its set-up.
            path: "pos",
            children: [
              { index: true, element: <Navigate to="register" replace /> },
              {
                path: "register",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PosRegisterPage />
                  </Suspense>
                )
              },
              {
                // The back office (PRD 118 Wave 5): tickets, drawers, the
                // floor log and the summary. Books roles read.
                path: "sales",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <PosSalesPage />
                  </Suspense>
                )
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <PosSettingsPage />
                  </Suspense>
                )
              },
              {
                path: "settings/scanner-sheet",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <ScannerSetupSheet />
                  </Suspense>
                )
              }
            ]
          },
          {
            // Personal services (PRD 121 §5.2). The bare path is PRD 122's
            // calendar: one location's day, by provider.
            path: "appointments",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <AppointmentCalendarPage />
                  </Suspense>
                )
              },
              {
                path: "overview",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PersonalServicesOverviewPage />
                  </Suspense>
                )
              },
              // PRD 122 Wave 5: standing appointments and Couldn't book (?view=conflicts).
              {
                path: "standing",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <StandingAppointmentsPage />
                  </Suspense>
                )
              },
              // PRD 126 W1: the Checkout row's landing, one appointment's ticket, and today's takings.
              {
                path: "checkout",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <CheckoutIndexPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":appointmentId",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <CheckoutPage />
                      </Suspense>
                    )
                  }
                ]
              },
              {
                path: "takings",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <TakingsPage />
                  </Suspense>
                )
              },
              // PRD 126 W2: Tips (books roles) and My tips (a provider's own).
              {
                path: "tips",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <TipsPage />
                  </Suspense>
                )
              },
              {
                path: "my-tips",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <MyTipsPage />
                  </Suspense>
                )
              },
              {
                path: "setup",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PersonalServicesSetUpPage />
                  </Suspense>
                )
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PersonalServicesSettingsPage />
                  </Suspense>
                )
              },
              // Wave 2 (§5.2): the locations, their hours, closures and rooms.
              {
                path: "locations",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <PersonalServicesLocationsPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <PersonalServicesLocationPage />
                      </Suspense>
                    )
                  },
                ]
              },
              // Wave 3 (§5.2): the service menu.
              {
                path: "menu",
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <PersonalServicesMenuPage />
                      </Suspense>
                    )
                  },
                  {
                    path: ":id",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <PersonalServicesMenuServicePage />
                      </Suspense>
                    )
                  },
                ]
              },
              // Wave 2 (§5.1): the shared roster in this module's words. The
              // scope mounts PS_ROSTER_MODULE above field service's pages.
              {
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <PersonalServicesRosterScope />
                  </Suspense>
                ),
                children: [
                  {
                    path: "staff",
                    children: [
                      {
                        index: true,
                        element: (
                          <Suspense fallback={<LoadingFallback variant="table" />}>
                            <TechniciansPage />
                          </Suspense>
                        )
                      },
                      {
                        path: ":id",
                        element: (
                          <Suspense fallback={<LoadingFallback />}>
                            <TechnicianPage />
                          </Suspense>
                        )
                      },
                    ]
                  },
                  {
                    path: "me",
                    element: (
                      <Suspense fallback={<LoadingFallback />}>
                        <MyProfilePage />
                      </Suspense>
                    )
                  },
                  {
                    path: "time-off",
                    element: (
                      <Suspense fallback={<LoadingFallback variant="table" />}>
                        <TimeOffPage />
                      </Suspense>
                    )
                  },
                ]
              }
            ]
          },
          {
            // The cannabis module (PRD 111 §5). Fixed paths first (Waves 1-2,
            // 4 and 5; Classification, Activities and Allocations take their
            // year as `?year=`), then Wave 3's `tax-years/:year` (the 280E schedule)
            // BELOW every fixed path, so a literal like "setup" is never read
            // as a year.
            path: "cannabis",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CannabisOverviewPage />
                  </Suspense>
                )
              },
              {
                path: "setup",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CannabisSetupPage />
                  </Suspense>
                )
              },
              {
                path: "settings",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CannabisSettingsPage />
                  </Suspense>
                )
              },
              {
                // Wave 2. Reached from the Overview's Classification card
                // and its tax-year rows.
                path: "classification",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <CannabisClassificationPage />
                  </Suspense>
                )
              },
              {
                // Wave 4: which activities 280E reaches, and how the costs
                // split. Reached from the Overview's tax-year rows, the
                // Classification header and the Schedule.
                path: "activities",
                element: (
                  <Suspense fallback={<LoadingFallback />}>
                    <CannabisActivitiesPage />
                  </Suspense>
                )
              },
              {
                // Wave 5 (slice 5a): cost pools and property functions.
                // Reached from the Overview's tax-year rows, Classification's
                // mixed rows, Activities' driver rows and the Schedule.
                path: "allocations",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <CannabisAllocationsPage />
                  </Suspense>
                )
              },
              {
                // Wave 3: the year's 280E schedule and federal estimate.
                // Reached from the Overview's year card and tax-year rows,
                // and from the Classification screen.
                path: "tax-years/:year",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <CannabisSchedulePage />
                  </Suspense>
                )
              }
            ]
          },
          {
            // Saved non-inventory lines (PRD 58). Index only — an item is
            // four fields, edited in a drawer, with no history to show on a
            // detail page: nothing accrues to it.
            path: "service-items",
            element: (
              <Suspense fallback={<LoadingFallback variant="table" />}>
                <ServiceItemsList />
              </Suspense>
            )
          },
          {
            // PRD 04 §4.1 routes these under /settings. They sit beside the
            // other things you TAG transactions against instead — customers,
            // vendors, inventory items — because a property is an object you
            // act on, not a preference you set once.
            path: "properties",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <PropertiesList />
                  </Suspense>
                )
              },
              {
                path: ":propertyId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <PropertyDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            // Business-wide, like leases: "what is broken anywhere" is the
            // question, and the property filter gets back down to one.
            path: "maintenance",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <MaintenanceQueue />
                  </Suspense>
                )
              },
              {
                path: ":requestId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="skeleton" />}>
                    <MaintenanceDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            // Business-wide rather than nested under /properties/:id: a
            // landlord asks "who is up for renewal" long before "what is
            // happening at 400 Elm St", and a rent roll spans buildings. The
            // property filter is how you get back down to one.
            path: "leases",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LeasesList />
                  </Suspense>
                )
              },
              {
                // BEFORE `:leaseId`, or "renewals" is read as a lease id and
                // the page 404s on a lookup that was never a lookup.
                path: "renewals",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <RenewalPipeline />
                  </Suspense>
                )
              },
              {
                // BEFORE `:leaseId` for the same reason as "renewals"
                // above: otherwise "letters-of-credit" is read as a lease
                // id and the page 404s.
                path: "letters-of-credit",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LetterOfCreditWatch />
                  </Suspense>
                )
              },
              {
                path: ":leaseId",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LeaseDetail />
                  </Suspense>
                )
              }
            ]
          },
          {
            path: "reports",
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<LoadingFallback variant="list" />}>
                    <ReportsIndex />
                  </Suspense>
                )
              },
              // Two reports listed on the Reports index live under
              // `accounting/` for historical reasons, so the URL a reader
              // would GUESS — and the one they would type from memory after
              // seeing the others — 404s. The index's own links already point
              // at the canonical paths; these only catch a hand-typed or
              // bookmarked guess and send it to the same place.
              {
                path: "trial-balance",
                element: <Navigate to="/business/accounting/trial-balance" replace />
              },
              {
                path: "general-ledger",
                element: <Navigate to="/business/accounting/chart-of-accounts" replace />
              },
              {
                path: "profit-and-loss",
                element: (
                  <Suspense fallback={<LoadingFallback variant="chart" />}>
                    <ProfitAndLoss />
                  </Suspense>
                )
              },
              // PRD 137 Wave 3. Out until the forecasting module is released
              // (segments/release.ts). Under /business/reports, so
              // LIVE_ROUTE_PREFIXES already covers it.
              ...(SEGMENTS_RELEASED
                ? [
                    {
                      path: "profit-and-loss-by-segment",
                      element: (
                        <Suspense fallback={<LoadingFallback variant="table" />}>
                          <LiveProfitAndLossBySegment />
                        </Suspense>
                      )
                    }
                  ]
                : []),
              {
                path: "balance-sheet",
                element: (
                  <Suspense fallback={<LoadingFallback variant="chart" />}>
                    <BalanceSheet />
                  </Suspense>
                )
              },
              // PRD 83 Wave 3: the nonprofit statements and release from
              // restriction. Always routed; each page shows the api's refusal
              // (framework, funds off, Fund accounting not active) as it stands.
              // Under /business/reports, so LIVE_ROUTE_PREFIXES covers them.
              {
                path: "statement-of-financial-position",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LiveStatementOfFinancialPosition />
                  </Suspense>
                )
              },
              {
                path: "statement-of-activities",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LiveStatementOfActivities />
                  </Suspense>
                )
              },
              {
                path: "releases-from-restriction",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <FundReleasesPage />
                  </Suspense>
                )
              },
              {
                path: "ar-aging",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <ARAgingReport />
                  </Suspense>
                )
              },
              {
                path: "ap-aging",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <APAgingReport />
                  </Suspense>
                )
              },
              {
                // Live mode only — no sample-data preview exists for it
                path: "cash-flow",
                element: (
                  <Suspense fallback={<LoadingFallback variant="chart" />}>
                    <LiveCashFlowStatement />
                  </Suspense>
                )
              },
              {
                path: "inventory-valuation",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <InventoryValuationReport />
                  </Suspense>
                )
              },
              // PRD 41: a group's combining schedules. Out until the api it needs
              // is on prod (combiningLive).
              ...(COMBINED_REPORTING_LIVE
                ? [
                    {
                      path: "combining",
                      element: (
                        <Suspense fallback={<LoadingFallback variant="table" />}>
                          <CombiningSchedulePage />
                        </Suspense>
                      )
                    }
                  ]
                : []),
              {
                // Live mode only — the preview build has no sample rent roll.
                // Reached from three places on the business dashboard.
                path: "rent-roll",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LiveRentRoll />
                  </Suspense>
                )
              },
              {
                // PRD 102 §4.10: field service memberships' deferred revenue
                // and its tie to the ledger. No module gate: a lapsed business
                // still explains a balance on its own balance sheet.
                path: "deferred-membership-revenue",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <DeferredMembershipRevenuePage />
                  </Suspense>
                )
              },
              {
                // PRD 118 Wave 5. A core report: invoices and register sales
                // receipts both, so not gated on point of sale.
                path: "sales-by-item",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LiveSalesByItem />
                  </Suspense>
                )
              },
              {
                // Reached from the property page, where the pools that feed it
                // are configured — the two only make sense together.
                path: "unit-cost-allocation",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LiveUnitCostAllocation />
                  </Suspense>
                )
              },
              {
                // PRD 56 §12. The 1099-K a business RECEIVES from its payment
                // processor, bridged to its books — not the 1099s it FILES for
                // vendors, which are at tax/1099 under Vendors. Under `reports`
                // because it is a year-end statement someone reads, and because
                // the person doing it is already in the reports section.
                path: "1099k-reconciliation",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <Live1099kReconciliation />
                  </Suspense>
                )
              },
              {
                // PRD 99 §4.11 (Wave 3). Tips owed to staff, collected to paid,
                // tied to the ledger; Record tip payout. Core, not gated.
                path: "tips-payable",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <TipsPayableReport />
                  </Suspense>
                )
              },
              {
                // PRD 110 §4.6 (Waves 3–4). Retainage customers hold back and
                // retainage held from vendors, with the account's tie. Core.
                path: "retainage-receivable",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <RetainageReceivableReport />
                  </Suspense>
                )
              },
              {
                path: "retainage-payable",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <RetainagePayableReport />
                  </Suspense>
                )
              },
              {
                // PRD 49 §3.5. The annual CAM true-up. Under `reports` rather
                // than under a property because a landlord reconciles a YEAR,
                // and the property is the filter — and because that prefix is
                // already in LiveBusinessShell's live list, so it does not
                // draw a "sample data" banner over real documents.
                path: "recovery-reconciliation",
                element: (
                  <Suspense fallback={<LoadingFallback variant="table" />}>
                    <LiveRecoveryReconciliation />
                  </Suspense>
                )
              }
            ]
          }
        ]
      },
      {
        path: "chatbot",
        element: (
          <Suspense fallback={<LoadingFallback variant="dashboard" />}>
            <DemoGate message={DEMO_FRANKLIN_OFF_COPY}><ChatbotPage /></DemoGate>
          </Suspense>
        )
      },
      {
        path: "settings",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <Settings />
          </Suspense>
        )
      },
      {
        // The "Manage" link in every digest footer points here. The component
        // has existed all along but was never routed, so the link 404'd.
        path: "settings/notifications",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <NotificationPreferencesPage />
          </Suspense>
        )
      },
      {
        // The in-app inbox. Built with the port from mobile and left unrouted,
        // so on web every in-app notification — every work reminder among
        // them — went somewhere nobody could open: email and push are off by
        // default. The sidebar's Notifications row is how it is reached.
        path: "notifications",
        element: (
          <Suspense fallback={<LoadingFallback variant="list" />}>
            <NotificationsPage />
          </Suspense>
        )
      },
      {
        path: "privacy",
        element: (
          <Suspense fallback={<LoadingFallback variant="skeleton" />}>
            <PrivacyPage />
          </Suspense>
        )
      },
      {
        path: "terms",
        element: (
          <Suspense fallback={<LoadingFallback variant="skeleton" />}>
            <TermsPage />
          </Suspense>
        )
      },
      {
        path: "subscription",
        element: (
          <Suspense fallback={<LoadingFallback variant="skeleton" />}>
            <DemoGate><SubscriptionManagePage /></DemoGate>
          </Suspense>
        )
      }
    ]
  },
  {
    // Developer tooling — reachable from Settings → Developer in local
    // and QA builds; never shipped reachable in production
    path: "/design-system",
    element: IS_DEV_OR_QA_BUILD ? (
      <Suspense fallback={<LoadingFallback variant="skeleton" />}>
        <DesignSystemPage />
      </Suspense>
    ) : (
      <Navigate to="/" replace />
    ),
  },
  {
    path: "/subscribe",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DemoGate><SubscribePage /></DemoGate>
      </Suspense>
    ),
  },
  {
    path: "/waitlist",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BetaWaitlistPage />
      </Suspense>
    ),
  },
  {
    path: "/welcome",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <WelcomePage />
      </Suspense>
    ),
  },
  {
    // The Live sample (PRD 117, greenbackapp/docs#151). Public: the visitor
    // comes from a marketing page with no account. Only does anything on the
    // demo api, the one place POST /demo/sessions exists.
    path: "/demo/ended",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DemoEndedPage />
      </Suspense>
    ),
  },
  {
    // The chooser: marketing pages without a sample of their own link here.
    path: "/demo",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DemoStartPage />
      </Suspense>
    ),
  },
  {
    path: "/demo/:persona",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <DemoStartPage />
      </Suspense>
    ),
  },
  {
    path: "/login",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/register",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <RegisterPage />
      </Suspense>
    ),
  },
  {
    path: "/registration-success",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <RegistrationSuccess />
      </Suspense>
    ),
  },
  {
    path: "/verification-error",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <VerificationErrorPage />
      </Suspense>
    ),
  },
  {
    // The accountant activation screen (PRD 40 §5.5.2). Authenticated — it
    // creates a firm — but deliberately OUTSIDE the app shell: it is reached
    // straight from the invitation page, which is itself outside the shell,
    // and it renders its own full-page layout.
    //
    // NOW THE ONLY FIRM ROUTE OUT HERE, and not an oversight: every other one
    // moved into the shell so the Practice section can lead to it. This one
    // cannot follow, because that section only renders for someone who
    // already has a practice — which is precisely who is not on this screen.
    path: "/firm/activate",
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <DemoGate><AccountantActivationPage /></DemoGate>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    // Buying Greenback Business (PRD 86 slice 5): the plan, checkout and
    // naming the first business. OUTSIDE AppShell and BusinessShell, like
    // /firm/activate: the person has no books yet, and with
    // `business_accounting` off the business shell shows them only sample
    // data, so naming must not depend on either. Outside the personal paywall
    // too (paywallScope). The /business shell has no `start` child, so this
    // is the only route the path matches (navigationRoutes.test pins it).
    path: BUSINESS_START_PATH,
    errorElement: <FranklinErrorPage />,
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <DemoGate><BusinessStartPage /></DemoGate>
        </Suspense>
      </ProtectedRoute>
    ),
  },
  {
    // Public by design (PRD 28 §10.2): the invitation email has to work for
    // someone who has no Greenback account yet, so this sits outside
    // ProtectedRoute and outside the business shell.
    path: "/business/invitations/accept",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <InvitationAcceptPage />
      </Suspense>
    ),
  },
  {
    // The STAFF invitation email (PRD 40 §5.2). Public for the same reason as
    // the business one above, and outside the shell for the same reason
    // /firm/activate is: the visitor has no practice yet, so the Practice
    // section that would lead here does not render for them.
    //
    // MUST MATCH `Firms::DeliveryService`, which builds
    // "#{WEB_APP_URL}/firm/invitations/accept?token=...". It has built that
    // since PRD 40 Wave 1 and this route did not exist, so every invitation a
    // practice sent its own people landed on Not Found.
    path: "/firm/invitations/accept",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <FirmInvitationAcceptPage />
      </Suspense>
    ),
  },
  {
    // The ENGAGEMENT invitation email — a practice asking a prospective client
    // for their books (PRD 40 §5.5). Public: the recipient is not a customer
    // of ours yet, and there is no business context until they accept.
    //
    // MUST MATCH `Firms::EngagementDeliveryService`, which builds
    // "#{WEB_APP_URL}/business/engagements/accept?token=...". Same age and
    // same omission as the route above.
    path: "/business/engagements/accept",
    errorElement: <FranklinErrorPage />,
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <EngagementAcceptPage />
      </Suspense>
    ),
  },
  {
    // Public by design (PRD 37 §6): the visitor is a VENDOR, not a Greenback
    // user — no account, no session, no business context. The token in the
    // path authorizes this one form and is never an API credential.
    //
    // Short and top-level on purpose: this URL is typed by hand off a printed
    // email more often than anyone would like.
    path: "/w9/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <W9FormPage />
      </Suspense>
    ),
  },
  {
    // Public for the same reason as /w9 above (PRD 56 §7): the visitor is a
    // CUSTOMER of our customer, with no account, session or business context.
    // The token in the path authorizes reading and paying ONE invoice.
    //
    // Short and top-level on purpose: this URL is printed on the invoice PDF
    // and gets typed by hand. It must match InvoicePaymentLinks::Url in the
    // API, which builds "#{WEB_APP_URL}/pay/#{token}".
    path: "/pay/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PayInvoicePage />
      </Suspense>
    ),
  },
  {
    // The OAuth consent screen (TDD 2026-09-10 R2). The authorization server
    // is the API host; its browser half is here, because signing in to
    // Greenback is an emailed one-time code and this is where that works.
    //
    // NOT inside ProtectedRoute: that gate also enforces the paywall and the
    // beta waitlist, and neither belongs between somebody and granting access
    // to their own data. The page signs them in itself and comes back.
    path: "/oauth/consent",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <OAuthConsentPage />
      </Suspense>
    ),
  },
  {
    // Public for the same reason as /report-a-repair below (PRD 55 P1), and
    // short for the same reason: a tenant bookmarks this one. The token opens
    // ONE tenancy and is refused here unless it was issued for the portal —
    // a repair-only token gets the same generic refusal as a dead one.
    path: "/my-tenancy/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <MyTenancyPage />
      </Suspense>
    ),
  },
  {
    // Public for the same reason as /w9 (PRD 54 §4): the visitor is a TENANT
    // with no account, session or business context. Short and top-level on
    // purpose — this URL lives on a fridge magnet and gets typed by hand.
    path: "/report-a-repair/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ReportARepairPage />
      </Suspense>
    ),
  },
  {
    // PRD 63 Wave 3. Public for the same reason as the rest of these: the
    // visitor is often not the account holder at all — an office manager, a
    // spouse, a controller who was never invited.
    //
    // `/r/` rather than something descriptive, because this URL is READ ALOUD
    // and typed by hand more than any of the others: it arrives by email, and
    // the person answering is frequently on a phone with the laptop open
    // beside them.
    path: "/r/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ClientRequestPage />
      </Suspense>
    ),
  },
  {
    // PRD 72 §4.6 — one-click unsubscribe from a tenant's outreach. Public
    // for the same reason as /r/: the visitor is a stranger with a link.
    path: "/u/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <OutreachUnsubscribePage />
      </Suspense>
    ),
  },
  {
    // PRD 76 §6 — a tenant's lead form. Public: the visitor is a stranger on
    // the tenant's website, and the token IS the tenant. Short, because it is
    // embedded in an iframe and pasted into emails.
    path: "/f/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <LeadFormPage />
      </Suspense>
    ),
  },
  {
    // The same form through its kiosk link: an iPad at a conference booth,
    // which resets itself for the next visitor.
    path: "/k/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <LeadFormPage kiosk />
      </Suspense>
    ),
  },
  {
    // PRD 76 §4.5 — the cancel link in a booking invite. Above /b/:token so
    // the literal wins.
    path: "/b/cancel/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BookingCancelPage />
      </Suspense>
    ),
  },
  {
    // PRD 76 §4.5 — a member's booking page.
    path: "/b/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BookingPage />
      </Suspense>
    ),
  },
  {
    // PRD 92 §4.5 — a request's status link, from the confirmation screen and
    // the acknowledgement email. Above /book/:token so the literal wins.
    // Public: the visitor is the person who asked; the token is this request.
    path: "/book/status/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BookingStatusPage />
      </Suspense>
    ),
  },
  {
    // PRD 92 §4.5 — a business's online booking page, linked from its website,
    // embedded in an iframe (?embed=1) and pasted into its Google profile.
    // Public for the same reason as /f/: the visitor is a stranger, and the
    // token IS the tenant. Outside BusinessProvider; plain api* helpers.
    path: "/book/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <BookOnlinePage />
      </Suspense>
    ),
  },
  {
    // PRD 77 §4.3 — a tenant's proposal, read and signed by their customer.
    // Public for the same reason as /f/ and /r/: the visitor has no account,
    // and the token in the path authorizes this one proposal.
    path: "/p/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <ProposalPage />
      </Suspense>
    ),
  },
  {
    // PRD 94 §4.9 — a visit's page, linked from its confirmation and reminder
    // emails. Public for the same reason as /p/: the visitor is the business's
    // customer, and the token in the path authorizes this one visit.
    path: "/t/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <VisitPreviewPage />
      </Suspense>
    ),
  },
  {
    // PRD 107 — a customer's standing account link (`/c/:token`) and its
    // sub-pages (a place's history, a request, notifications). Public for the
    // same reason as /t/ and /e/: the visitor is the business's customer, and
    // the link IS the identity. Outside BusinessProvider; plain api* helpers.
    path: "/c/:token/*",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <CustomerHubPage />
      </Suspense>
    ),
  },
  {
    // PRD 104 §4.3 — a completed job's feedback page, linked from the one ask
    // (`/r/` is PRD 63's). Public for the same reason as /t/ and /e/: the
    // visitor is the business's customer, and the token authorizes this one ask.
    path: "/s/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <FeedbackPage />
      </Suspense>
    ),
  },
  {
    // PRD 125 §4.6 — the card page: a client keeps a card on file with a
    // business (two unticked boxes, then Stripe's own page) and removes it
    // again. Public for the same reason as /t/ and /e/: the visitor is the
    // business's client, and the token authorizes this one request or card.
    // Never gated — removing a card must always work.
    path: "/card/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <CardOnFilePage />
      </Suspense>
    ),
  },
  {
    // PRD 98 §4.7 — an estimate's page, linked from the email that sends it.
    // Public for the same reason as /t/ and /p/: the visitor is the business's
    // customer, and the token in the path authorizes this one estimate.
    path: "/e/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PublicEstimatePage />
      </Suspense>
    ),
  },
  {
    // Public for the same reason as /w9 above (PRD 47 §7): the visitor is a
    // TENANT, with no account, session or business context. The token in the
    // path authorizes this one lease and is never an API credential.
    path: "/lease-signing/:token",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <LeaseSigningPage />
      </Suspense>
    ),
  },
  {
    // Public by design: this is the "Unsubscribe" link in every digest footer,
    // clicked from a mail client by someone who is very often not signed in.
    // Requiring a login before you can stop email is not a real unsubscribe.
    // The signed token in the query string is the whole of the authorization.
    path: "/unsubscribe",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <UnsubscribePage />
      </Suspense>
    ),
  },
  {
    // Public by design (PRD 93 §4.3.2): Greenback's text messaging terms and
    // privacy notice, cited by the short code program brief and linked from
    // every opt-in wording — read by carriers' reviewers and customers, who
    // have no account.
    path: "/sms-terms",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <SmsTermsPage view="terms" />
      </Suspense>
    ),
  },
  {
    path: "/sms-terms/privacy",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <SmsTermsPage view="privacy" />
      </Suspense>
    ),
  }
];

const router = sentryCreateBrowserRouter(routes);

trackRouteChanges(router);

function App() {
  return (
    <TamaguiProvider config={tamaguiConfig} defaultTheme="dark">
    <PostHogProvider>
      <ThemeProvider>
        <PrintStyles />
        <ToastProvider>
          <AuthProvider>
            <PostHogIdentifier />
            <DemoReadOnlyListener />
            <SessionTimeoutProvider>
              <AppPreferencesProvider>
                <SidebarProvider>
                  <ErrorBoundary>
                    <SystemBanner />
                    <RouterProvider router={router} />
                  </ErrorBoundary>
                </SidebarProvider>
              </AppPreferencesProvider>
            </SessionTimeoutProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </PostHogProvider>
    </TamaguiProvider>
  );
}

export default App;
